/**
 * Workflow Builder Page
 * 
 * 드래그 앤 드롭으로 노드를 추가하고, 4방향 포트로 엣지를 연결합니다.
 */

import { isAdminRole, isProductAdminRole } from '../utils/role';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Badge, Button, Modal, Notice, Popover, Select, Tabs, useToast } from '../components/ui';
import InfoIcon from '../assets/icons/common/info.svg?react';
import RefreshIcon from '../assets/icons/common/refresh.svg?react';
import PlayIcon from '../assets/icons/common/play.svg?react';
import PauseIcon from '../assets/icons/common/pause.svg?react';
import CheckIcon from '../assets/icons/common/check.svg?react';
import SkipIcon from '../assets/icons/common/skip.svg?react';
import CopyIcon from '../assets/icons/common/copy.svg?react';
import EditIcon from '../assets/icons/common/edit.svg?react';
// 워크플로우 정보 항목 아이콘 - 같은 규격(24 · 선 1.8)으로 맞춘 세트
import UserIcon from '../assets/icons/common/person.svg?react';
import CalendarIcon from '../assets/icons/common/calendar.svg?react';
import UploadIcon from '../assets/icons/common/publish.svg?react';
import DescriptionIcon from '../assets/icons/common/description.svg?react';
// 오른쪽 패널 접기 버튼 · 접혔을 때 알약 버튼 아이콘
import PanelCollapseIcon from '../assets/icons/common/chevron-right.svg?react';
import GridIcon from '../assets/icons/common/grid.svg?react';
import ListIcon from '../assets/icons/common/list.svg?react';
// 챗봇 테마 미리보기 - 챗봇 아바타 (AI 반짝이 아이콘)
import BotIcon from '../assets/icons/flow/ai.svg?react';
import ShareIcon from '../assets/icons/common/share.svg?react';
import ChevronIcon from '../assets/icons/sidebar/chevron-down.svg?react';
import ChatIcon from '../assets/icons/dashboard/chat.svg?react';
import CloseIcon from '../assets/icons/common/close.svg?react';
import ThemeIcon from '../assets/icons/common/theme.svg?react';
import SettingIcon from '../assets/icons/common/setting.svg?react';
// 노드 선 아이콘 · 색 - 워크플로우 템플릿과 같은 매핑 사용 (components/workflow/FlowSteps)
import { NODE_STEPS } from '../components/workflow/FlowSteps';
import FlowDatabaseIcon from '../assets/icons/flow/database.svg?react';
import NodeDefaultIcon from '../assets/icons/common/grid.svg?react';
// 커넥터 로고 (컬러 SVG → ?react 로 쓰면 단색이 되므로 <img> 로 사용)
import googleDriveLogo from '../assets/icons/marketplace/google-drive.svg';
import gmailLogo from '../assets/icons/marketplace/gmail.svg';
import slackLogo from '../assets/icons/marketplace/slack.svg';
import notionLogo from '../assets/icons/marketplace/notion.svg';
import naverLogo from '../assets/icons/marketplace/naver.svg';
import emailConnectorLogo from '../assets/icons/marketplace/email-connector.svg';
import dropboxLogo from '../assets/icons/marketplace/dropbox.svg';
// 빈 캔버스 안내 일러스트 (관리자 / 사용자 테마별)
import adminEmptyCanvas from '../assets/illustrations/admin-empty-canvas.svg';
import userEmptyCanvas from '../assets/illustrations/user-empty-canvas.svg';
import { workflowApi, knowledgeBaseApi, Workflow, WorkflowNode, WorkflowEdge, KnowledgeBase, ChatbotTheme, WorkflowRunLog } from '../api';
import { ThemeEditorModal } from '../components/chat/ThemeEditorModal';
import { authApi, customerApi } from '../api';
import { teamApi, Team } from '../api/teams';
import { adminApi } from '../api/aiProvider';
import connectorApi from '../api/connectors';
import { getInstalledNodes, MARKETPLACE_ITEMS } from './MarketplacePage';
import {
  OutputConfigModal,
  NotifyConfigModal,
  MailTriggerConfigModal,
  ScheduleTriggerConfigModal,
  WebhookTriggerConfigModal,
  HttpCallConfigModal,
  EmailSendConfigModal,
  GoogleDriveConfigModal,
  GoogleDriveSteps,
  DropboxConfigModal,
  DropboxSteps,
  SlackSteps,
  NotionSteps,
  McpSteps,
  GmailSteps,
  getNodeSetupFooter,
  SlackConfigModal,
  JiraConfigModal,
  GmailConfigModal,
  EmailConnectorNodeModal,
  NotionConfigModal,
  NaverMailConfigModal,
  WebSearchConfigModal,
  McpConfigModal,
  type OutputNodeConfig,
  type NotifyNodeConfig,
  type MailTriggerConfig,
  type ScheduleTriggerConfig,
  type WebhookTriggerConfig,
  type HttpCallConfig,
  type EmailSendConfig,
  type GoogleDriveConfig,
  type DropboxConfig,
  type SlackConfig,
  type JiraConfig,
  type GmailConfig,
  type EmailConnectorNodeConfig,
  type NotionConfig,
  type NaverMailConfig,
  type WebSearchConfig,
  type McpConfig,
} from '../components/workflow/NodeConfigModals';
import '../styles/workflow.css'; // 실행 상태 표시 (.wf-run-status) - 워크플로우 목록과 공통
import './WorkflowBuilderPage.css';

// 노드 타입 정보 (마켓플레이스에서 가져옴)
interface NodeTypeInfo {
  type: string;
  label: string;
  icon: string;
  category: string;
}

// 카테고리 정보
const CATEGORY_INFO: Record<string, { label: string }> = {
  '트리거': { label: '트리거' },
  '액션': { label: '액션' },
  '커넥터': { label: '커넥터' },
};

// 노드 타입 → 아이콘 - 없으면 기존 이모지 사용
//   커넥터: 컬러 로고 이미지 (빌더에서만, 원래 색 그대로)
//   그 외: 워크플로우 템플릿과 같은 선 아이콘 + 색 (FlowSteps NODE_STEPS, WorkflowBuilderPage.css .wb-icon--{tone})
const CONNECTOR_LOGOS: Record<string, string> = {
  'google-drive': googleDriveLogo,
  'dropbox':      dropboxLogo,
  'gmail':        gmailLogo,
  'slack':        slackLogo,
  'notion':       notionLogo,
  'naver':        naverLogo,
  'email':        emailConnectorLogo
};

// 로고 · NODE_STEPS 둘 다 없는 노드는 기본 블록 아이콘 (이모지 대신)
const renderNodeIcon = (type: string) => {
  const logo = CONNECTOR_LOGOS[type];
  if (logo) return <span className="wb-icon"><img src={logo} alt="" /></span>;
  const step = NODE_STEPS[type];
  if (!step) return <span className="wb-icon wb-icon--slate"><NodeDefaultIcon /></span>;
  const Icon = step.icon;
  return <span className={`wb-icon wb-icon--${step.tone}`}><Icon /></span>;
};

// 예시 워크플로우 카드 - 단계 (type: NODE_ICONS 아이콘 사용, icon: 노드 타입이 아닌 단계용 직접 지정)
interface ExampleStep {
  label: string;
  type?: string;
  icon?: React.ReactNode;
}

const renderExampleFlow = (steps: ExampleStep[]) => (
  <div className="example-flow">
    {steps.map((step) => (
      <div key={step.label} className="example-step">
        <span className="example-step-icon">{step.icon ?? renderNodeIcon(step.type ?? '')}</span>
        <span className="example-step-label">{step.label}</span>
      </div>
    ))}
  </div>
);

const EXAMPLE_RAG_STEPS: ExampleStep[] = [
  { label: '채팅 시작', type: 'trigger' },
  { label: '문서 검색', type: 'knowledge-retrieval' },
  { label: 'AI 응답', type: 'llm' },
];

const EXAMPLE_DB_REPORT_STEPS: ExampleStep[] = [
  { label: '스케줄', type: 'schedule-trigger' },
  { label: 'DB 조회', icon: <span className="wb-icon wb-icon--blue"><FlowDatabaseIcon /></span> },
  { label: '이메일 발송', type: 'email' },
];

const EXAMPLE_SLACK_ALERT_STEPS: ExampleStep[] = [
  { label: '채팅 시작', type: 'trigger' },
  { label: '웹 검색', type: 'web-search' },
  { label: 'Slack 전송', type: 'slack' },
];

// 예시 워크플로우가 필요로 하는 노드 타입 목록
const EXAMPLE_RAG_REQUIRED_NODES = ['chat_start', 'search', 'ai'];
const EXAMPLE_DB_REPORT_REQUIRED_NODES = ['schedule', 'search', 'email'];
const EXAMPLE_SLACK_ALERT_REQUIRED_NODES = ['chat_start', 'ddg', 'slack'];

// 실행 로그 상세 - 실행된 노드 이름 (트리거 노드는 표시 안 함)
const RUN_LOG_NODE_LABELS: Record<string, string> = {
  'knowledge-retrieval': 'DB 조회',
  'gmail': '메일 발송',
  'email': '메일 발송',
  'naver': '메일 발송',
  'email-send': '메일 발송',
  'notion': 'Notion',
  'google-drive': 'Drive',
  'web-search': '웹검색',
  'http-call': 'HTTP',
  'slack': 'Slack',
  'llm': 'AI 응답',
  'output': '답변',
  'notify': '알림',
};

// 챗봇 테마 설정 - 색 입력 (색 견본 칸 + 코드 입력칸)
//   견본 칸 값 = 설정값 없으면 기본색, 코드 입력칸 = 설정값 그대로 (빈 값이면 기본색이 placeholder)
const ThemeColorField = ({ label, value, fallback, onChange }: {
  label: string;
  value?: string;
  fallback: string;
  onChange: (value: string) => void;
}) => (
  <div className="wb-theme-field">
    <label>{label}</label>
    <div className="wb-theme-color">
      <span className="wb-theme-swatch">
        <input type="color" value={value || fallback} onChange={(e) => onChange(e.target.value)} />
      </span>
      <input type="text" className="wb-theme-input" placeholder={fallback} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

// 2026. 10. 2. 오후 2:45 형식 (초 단위 제외)
const formatDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '-';

// 마켓플레이스 설치 노드를 워크플로우 빌더 노드 타입으로 변환
function getNodeTypes(): NodeTypeInfo[] {
  const installedNodes = getInstalledNodes();
  return installedNodes.map((item) => ({
    type: item.nodeType,
    label: item.title,
    icon: item.icon,
    category: item.category === 'chatbot' ? '트리거' : item.category === 'trigger' ? '트리거' : item.category === 'action' ? '액션' : '커넥터',
  }));
}

// 카테고리별로 노드 그룹화
function getNodeTypesByCategory(): Record<string, NodeTypeInfo[]> {
  const nodeTypes = getNodeTypes();
  const grouped: Record<string, NodeTypeInfo[]> = {
    '트리거': [],
    '액션': [],
    '커넥터': [],
  };
  
  nodeTypes.forEach((node) => {
    if (grouped[node.category]) {
      grouped[node.category].push(node);
    } else {
      grouped['액션'].push(node); // 기본값
    }
  });
  
  return grouped;
}

type PortType = 'in' | 'out' | 'top' | 'bottom';

interface ConnectingLine {
  fromNodeId: string;
  fromPort: PortType;
  toX: number;
  toY: number;
}

// AI 응답 노드 기본 시스템 프롬프트
const DEFAULT_LLM_SYSTEM_PROMPT = `## 답변 품질 규칙
0. **사용자 친화적**: 딱딱하지 않게, 친절하고 읽기 쉬운 어조로 답변
1. **풍부한 답변**: 충분한 설명과 근거 포함
2. **구조화**: 마크다운 형식 적극 활용 — 표(table), 비교표, 리스트, 볼드, 헤더 등
- 데이터 비교 시 반드시 표(| 항목 | 값 |) 사용
- 수치가 3개 이상이면 표로 정리
- 장단점, 비교분석은 표로 시각화
3. **이모지 활용**: 📊 📈 💡 🔍 ⚡ 🏭 🎯 ✅ ⚠️ 🚀 등
4. **구체적 수치**: 가능하면 데이터 포함
5. **한국어**: 항상 한국어 답변
6. **전문성**: 컨설턴트 수준

## 🗓️ 날짜/시간 기준 규칙
- 오늘 날짜는 시스템 프롬프트 상단에 항상 제공됩니다.
- "이번달", "이번주", "오늘", "최근", "요즘" 등 상대적 시간 표현이 있으면 제공된 오늘 날짜를 기준으로 계산하세요.
- 검색 결과나 문서의 날짜가 요청한 기간에 해당하지 않으면 반드시 제외하세요.
- 해당 기간의 정보를 찾을 수 없으면 "해당 기간의 정보를 찾을 수 없습니다"라고 안내하세요.

## 차트/그래프 응답 규칙
### 응답 형식 분기
| 사용자 표현 | 응답 형식 |
|---|---|
| "표", "테이블", "목록" | 마크다운 테이블 |
| "그래프", "차트", "시각화", "그려줘" | charts JSON 코드블록 |
| 데이터 1~2건 | 마크다운만 사용. 차트 생성 금지 |

### 차트 응답 형식
사용자가 차트/그래프를 요청하면 아래 형식의 JSON 코드블록으로 응답하세요:
\`\`\`charts
[{"type":"bar","title":"차트 제목","labels":["라벨1","라벨2","라벨3"],"datasets":[{"label":"지표명","data":[100,200,300]}]}]
\`\`\`
필수 규칙:
- 반드시 \`\`\`charts 코드블록으로 감쌀 것
- HTML을 생성하지 말 것. JSON만 제공
- type: bar, line, pie, doughnut 등 Chart.js 지원 타입
- labels 값은 일렬로 작성, 줄바꿈 금지
- datasets의 data는 반드시 숫자 배열

### 색상 팔레트
- bar/line 기본 순서: 테마 primaryColor 우선, 이후 #4F8EF7, #F7A14F, #6BCB77, #FF6B6B, #A78BFA, #F472B6, #34D399, #FBBF24`;

// AI 응답 노드 설정 타입
interface LLMNodeConfig {
  provider?: 'bedrock' | 'anthropic' | 'google';
  models?: string[];      // 선택된 모델 목록 — 첫번째가 기본값 (챗봇에서 사용자 선택 가능)
  temperature?: string;
  systemPrompt?: string;
  responseLanguage?: string;
  responseFormat?: string;
  responseLength?: string;
  maxTokens?: number;
  contextWindow?: string;
  streaming?: boolean;
  showSources?: boolean;
  teamId?: string;
  customerId?: string;
}

// 문서 검색 노드 설정 타입
interface KnowledgeRetrievalConfig {
  knowledgeBaseId?: string;
  topK?: number;
  similarityThreshold?: number;
  // text2sql 전용
  useText2Sql?: boolean;             // Text-to-SQL 모드 사용 여부 (사용자가 직접 토글)
  fixedQuery?: string;
  fixedQueryType?: 'natural' | 'sql';  // 자연어 or SQL 직접입력
  // 출력 형식
  outputFormat?: 'auto' | 'text_summary' | 'html_table' | 'markdown_table' | 'json' | 'csv';
}

// 판단 노드 설정 타입
interface ConditionNodeConfig {
  conditionType?: 'similarity_score' | 'keyword_match' | 'intent_classification' | 'custom';
  // 유사도 점수 기반
  similarityThreshold?: number;
  similarityOperator?: 'gte' | 'lte' | 'gt' | 'lt';
  // 키워드 매칭
  keywords?: string;
  keywordMatchType?: 'any' | 'all' | 'none';
  // 의도 분류
  intents?: string;
  // 커스텀 조건
  customCondition?: string;
  // 분기 설정
  trueBranch?: string;  // 조건 만족 시 메시지
  falseBranch?: string; // 조건 불만족 시 메시지
}

// 모든 노드 설정 타입 통합
type NodeConfigUnion = LLMNodeConfig | KnowledgeRetrievalConfig | ConditionNodeConfig | 
  OutputNodeConfig | NotifyNodeConfig | MailTriggerConfig |
  WebhookTriggerConfig | ScheduleTriggerConfig | HttpCallConfig |
  EmailSendConfig | GoogleDriveConfig | SlackConfig | JiraConfig | GmailConfig | NotionConfig | NaverMailConfig | WebSearchConfig | McpConfig;

// 마크다운 → HTML 변환 (출력 결과 렌더링용)
function markdownToHtml(md: string): string {
  return md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^#{3}\s+(.+)$/gm, '<h3 style="margin:10px 0 4px;font-size:14px;">$1</h3>')
    .replace(/^#{2}\s+(.+)$/gm, '<h2 style="margin:12px 0 6px;font-size:15px;">$1</h2>')
    .replace(/^#{1}\s+(.+)$/gm, '<h1 style="margin:14px 0 6px;font-size:16px;">$1</h1>')
    // 마크다운 표 변환
    .replace(/^\|\s*(.*?)\s*\|$/gm, (line) => {
      if (/^\|[\s\-:|]+\|$/.test(line)) return '__TABLE_SEP__';
      const cells = line.split('|').filter((_, i, a) => i > 0 && i < a.length - 1);
      return `<tr>${cells.map(c => `<td style="padding:5px 10px;border:1px solid #e2e8f0;">${c.trim()}</td>`).join('')}</tr>`;
    })
    .replace(/__TABLE_SEP__\n?/g, '')
    .replace(/(<tr>.*?<\/tr>\n?)+/gs, (rows) => {
      // 첫 번째 tr을 thead로
      const firstEnd = rows.indexOf('</tr>') + 5;
      const firstRow = rows.slice(0, firstEnd).replace(/<td/g, '<th style="background:#f1f5f9;padding:5px 10px;border:1px solid #e2e8f0;font-weight:600;"').replace(/<\/td>/g, '</th>');
      const rest = rows.slice(firstEnd);
      return `<table style="border-collapse:collapse;width:100%;margin:8px 0;"><thead>${firstRow}</thead><tbody>${rest}</tbody></table>`;
    })
    // 목록
    .replace(/^[-*]\s+(.+)$/gm, '<li style="margin:2px 0;">$1</li>')
    .replace(/(<li.*?<\/li>\n?)+/gs, (items) => `<ul style="margin:6px 0 6px 20px;">${items}</ul>`)
    // 줄바꿈
    .replace(/\n\n/g, '</p><p style="margin:4px 0;">')
    .replace(/\n/g, '<br>');
}

export function WorkflowBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();  const fromTemplates = (location.state as any)?.fromTemplates === true;
  const { showToast } = useToast();
  const canvasRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // 방금 create 한 워크플로우 id - URL 교체 후 loadData 재조회를 건너뛰기 위함
  const justCreatedRef = useRef<string | null>(null);

  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // AI 제공자 연결 상태
  const [connectedProviders, setConnectedProviders] = useState<Array<{ id: string; name: string }>>([]);
  // Bedrock 사용 가능한 모델 목록
  const [bedrockModels, setBedrockModels] = useState<Array<{ modelId: string; modelName: string; providerName: string }>>([]);
  // Google AI 사용 가능한 모델 목록
  const [googleModels, setGoogleModels] = useState<Array<{ modelId: string; modelName: string; providerName: string }>>([]);
  // LLM 모달 팀 선택 (tenantAdmin/customerAdmin 전용)
  const [llmTeamList, setLlmTeamList] = useState<Team[]>([]);
  const [llmTeamModelsLoading, setLlmTeamModelsLoading] = useState(false);
  // 팀 그룹 (LLM · 공유 모달 공통) — productAdmin/tenantAdmin: 고객사별 그룹 / customerAdmin: 단일
  const [llmTeamGroups, setLlmTeamGroups] = useState<{ customerId: string; customerName: string; teams: Team[] }[]>([]);
  const [shareTeamGroups, setShareTeamGroups] = useState<{ customerId: string; customerName: string; teams: Team[] }[]>([]);

  // 드래그 상태
  const [draggedPaletteItem, setDraggedPaletteItem] = useState<{ type: string; label: string; icon: string } | null>(null);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // 선 연결 상태
  const [connectingLine, setConnectingLine] = useState<ConnectingLine | null>(null);

  // 노드 설정 모달 상태
  const [configModalNode, setConfigModalNode] = useState<WorkflowNode | null>(null);
  const [configModalShowErrors, setConfigModalShowErrors] = useState(false);
  const [tempNodeConfig, setTempNodeConfig] = useState<NodeConfigUnion>({});

  // 테마 설정 모달 상태
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [tempTheme, setTempTheme] = useState<ChatbotTheme>({});
  const [savingTheme, setSavingTheme] = useState(false);

  // 워크플로우 정보 수정 모달 상태
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [tempWorkflowInfo, setTempWorkflowInfo] = useState({ name: '', description: '' });
  const [savingInfo, setSavingInfo] = useState(false);
  // 상단 제목 · 설명 인라인 편집 (클릭 → input, blur · Enter 시 자동 저장, ESC 취소)
  const [inlineEdit, setInlineEdit] = useState<'name' | 'description' | null>(null);
  const [inlineDraft, setInlineDraft] = useState('');

  // 헤더 워크플로우 정보 팝오버
  const [showInfoPopover, setShowInfoPopover] = useState(false);

  // 오른쪽 패널(예시 워크플로우 / 실행 로그) 접힘 상태
  const [sidePanelCollapsed, setSidePanelCollapsed] = useState(false);

  // 팔레트 카테고리 접기 상태
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});

  // 실행 로그 패널 상태
  const [showLogPanel, setShowLogPanel] = useState(false);
  const [runLogs, setRunLogs] = useState<WorkflowRunLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [selectedLog, setSelectedLog] = useState<WorkflowRunLog | null>(null);
  const [selectedLogTab, setSelectedLogTab] = useState<'query' | 'result'>('query');

  // 스케줄 활성 상태
  const [isScheduleActive, setIsScheduleActive] = useState(false);
  const [scheduleActing, setScheduleActing] = useState(false);
  // 저장 확인 모달 (스케줄 워크플로우 저장 시 실행 여부 선택)
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [saveScheduleActive, setSaveScheduleActive] = useState(true);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');

  // 예시 불러오기 확인 모달 상태
  const [exampleConfirm, setExampleConfirm] = useState<'rag' | 'db' | 'slack' | null>(null);

  // 지금 실행 확인 모달 상태
  const [runConfirmOpen, setRunConfirmOpen] = useState(false);
  // 복사하기 - 커넥터 안내 모달
  const [showCopyConnectorModal, setShowCopyConnectorModal] = useState(false);
  
  // 모달 드래그 상태
  const [themeModalPos, setThemeModalPos] = useState({ x: 0, y: 0 });
  const [infoModalPos, setInfoModalPos] = useState({ x: 0, y: 0 });
  const [configModalPos, setConfigModalPos] = useState({ x: 0, y: 0 });
  const [isDraggingModal, setIsDraggingModal] = useState<'theme' | 'info' | 'config' | null>(null);
  const [modalDragStart, setModalDragStart] = useState({ x: 0, y: 0 });

  const currentUser = authApi.getUser();
  const isAdmin = isAdminRole(currentUser?.role);
  const isProductAdmin = isProductAdminRole(currentUser?.role);

  // 편집 가능 여부: 새 워크플로우 · 본인 소유 · 제품관리자
  const canEdit = workflow?.id === 'new' || !workflow?.owner || workflow?.owner === currentUser?.email || isProductAdmin;

  // 복사하기: 미설치 노드 체크 → 커넥터 안내 → id=new 상태로 열기 (저장 안 함)
  // 커넥터 노드 타입 (사용자별 별도 설정 필요한 것들)
  const CONNECTOR_NODE_TYPES = new Set(['slack', 'notion', 'google-drive', 'dropbox', 'gmail', 'email', 'mcp', 'naver-mail', 'jira']);

  const handleCopy = () => {
    if (!workflow) return;

    // 1) 미설치 노드 체크
    const installedNodeTypes = new Set(getInstalledNodes().map(n => n.nodeType));
    const marketplaceNodeTypes = new Set(MARKETPLACE_ITEMS.map(m => m.nodeType));
    const missingTypes = [...new Set(workflow.nodes.map(n => n.type))]
      .filter(t => marketplaceNodeTypes.has(t) && !installedNodeTypes.has(t));

    if (missingTypes.length > 0) {
      const typeToName: Record<string, string> = {};
      MARKETPLACE_ITEMS.forEach(m => { typeToName[m.nodeType] = m.title; });
      const names = missingTypes.map(t => typeToName[t] || t).join(', ');
      showToast('error', `미설치 노드가 포함되어 있습니다 ( ${names} ) \n마켓플레이스에서 설치 후 복사해 주세요.`);
      return;
    }

    // 2) 커넥터 노드 여부 체크
    const hasConnector = workflow.nodes.some(n => CONNECTOR_NODE_TYPES.has(n.type));
    if (hasConnector) {
      setShowCopyConnectorModal(true);
      return;
    }

    // 3) 바로 복사 (저장 안 하고 열기)
    openAsDraft();
  };

  const openAsDraft = () => {
    if (!workflow) return;
    navigate('/workflows/new', {
      state: {
        templateName: workflow.name,
        templateNodes: workflow.nodes,
        templateEdges: workflow.edges,
      },
    });
  };

  // 브라우저 뒤로가기/탭 닫기 시 미저장 경고
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (workflow?.id === 'new' && workflow?.nodes?.length > 0) {
        e.preventDefault();
        e.returnValue = '저장하지 않으면 변경사항이 사라집니다.';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [workflow?.id, workflow?.nodes]);

  useEffect(() => {
    if (id === 'new') {
      // 새 워크플로우: DB 저장 없이 빈 상태로 시작
      // location.state에 템플릿 데이터가 있으면 복사본으로 초기화
      const tplState = (location.state as { templateName?: string; templateNodes?: WorkflowNode[]; templateEdges?: any[] } | null);
      setWorkflow({
        id: 'new',
        name: tplState?.templateName ? `${tplState.templateName} (복사본)` : '',
        description: '',
        nodes: tplState?.templateNodes || [],
        edges: tplState?.templateEdges || [],
        status: 'draft' as any,
        knowledgeBaseIds: [],
        owner: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as any);
      knowledgeBaseApi.list({ includeSystem: true }).then(setKnowledgeBases).catch(() => {});
      setLoading(false);
    } else if (id) {
      // 방금 저장(create)해서 URL이 교체된 경우: 현재 상태를 신뢰하고 DB 재조회 생략
      // (DynamoDB eventual consistency 로 재조회 시 노드가 사라지는 버그 방지)
      if (justCreatedRef.current === id) {
        justCreatedRef.current = null;
        setLoading(false);
        return;
      }
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 마운트 시 마켓플레이스 설치 목록을 서버에서 동기화 (다른 계정 로그인 시 localStorage 캐시 불일치 방지)
  const [paletteVersion, setPaletteVersion] = useState(0);
  useEffect(() => {
    const syncMarketplace = async () => {
      try {
        const { apiClient } = await import('../api');
        const res = await apiClient.get<{ success: boolean; installedIds: string[] }>('/api/marketplace/installed');
        const DEFAULT_IDS = ['chat_start', 'manual_run', 'ai', 'search', 'cond'];
        const installedIds = (res.data.installedIds && res.data.installedIds.length > 0)
          ? res.data.installedIds
          : DEFAULT_IDS;
        const { authApi } = await import('../api');
        const userId = authApi.getUser()?.id || 'guest';
        const key = `marketplace_installed_${userId}`;
        const current = localStorage.getItem(key);
        if (current !== JSON.stringify(installedIds)) {
          localStorage.setItem(key, JSON.stringify(installedIds));
          setPaletteVersion(v => v + 1); // 팔레트 강제 재렌더
        }
      } catch { /* 실패 시 기존 캐시 유지 */ }
    };
    syncMarketplace();
  }, []);

  // AI 제공자 연결 상태 로드
  useEffect(() => {
    const loadProviders = async () => {
      const providers: Array<{ id: string; name: string }> = [];
      const currentUser = authApi.getUser();
      const role = currentUser?.role || '';
      const needsTeamSelect = ['tenantAdmin', 'customerAdmin', 'productAdmin', 'admin'].includes(role);

      // tenantAdmin/customerAdmin/productAdmin은 팀 선택 후 모델 로드 → 초기 Bedrock 로드 스킵
      if (!needsTeamSelect) {
        try {
          const models = await adminApi.listBedrockModels();
          setBedrockModels(models);
          if (models.length > 0) {
            providers.push({ id: 'bedrock', name: 'Amazon Bedrock' });
          }
        } catch {
          setBedrockModels([]);
        }
      }

      // Google AI (role 무관)
      try {
        const status = await adminApi.getProviderStatus('google');
        if (status.connected) {
          providers.push({ id: 'google', name: 'Google AI' });
          const models = await adminApi.listGoogleModels();
          setGoogleModels(models);
        }
      } catch {
        setGoogleModels([]);
      }

      setConnectedProviders(providers);

      // 팀 선택 필요한 role이면 팀 목록 로드 (LLM모달: Bedrock계정 보유 팀 / 공유모달: 전체 팀)
      if (needsTeamSelect) {
        try {
          let customerGroups: { customerId: string; customerName: string; allTeams: Team[] }[] = [];

          if (role === 'customerAdmin' && currentUser?.customerId) {
            const teams = await teamApi.listByCustomer(currentUser.customerId).catch(() => []);
            customerGroups = [{ customerId: currentUser.customerId, customerName: '', allTeams: teams }];
          } else if (['tenantAdmin', 'productAdmin', 'admin'].includes(role)) {
            const customers = await customerApi.list().catch(() => []);
            customerGroups = await Promise.all(
              customers.map(async c => ({
                customerId: c.id,
                customerName: c.name,
                allTeams: await teamApi.listByCustomer(c.id).catch(() => []),
              }))
            );
          }

          // 공유 모달용: 전체 팀 그룹
          setShareTeamGroups(
            customerGroups.filter(g => g.allTeams.length > 0).map(g => ({
              customerId: g.customerId,
              customerName: g.customerName,
              teams: g.allTeams,
            }))
          );

          // LLM 모달용: Bedrock 계정 있는 팀만
          const accounts = await adminApi.listBedrockAccounts().catch(() => []);
          const teamScopesWithAccounts = new Set(
            accounts.filter(a => a.scope?.startsWith('team:')).map(a => a.scope.replace('team:', ''))
          );
          const allTeams = customerGroups.flatMap(g => g.allTeams);
          setLlmTeamList(allTeams.filter(t => teamScopesWithAccounts.has(t.id)));
          setLlmTeamGroups(
            customerGroups.map(g => ({
              customerId: g.customerId,
              customerName: g.customerName,
              teams: g.allTeams.filter(t => teamScopesWithAccounts.has(t.id)),
            })).filter(g => g.teams.length > 0)
          );
        } catch { /**/ }
      }

      // 팀관리자: 공유 모달용 팀 목록 별도 로드 (Bedrock 연동과 무관하게 항상 로드)
      if (role === 'teamAdmin' && currentUser?.customerId) {
        try {
          const teams = await teamApi.listByCustomer(currentUser.customerId).catch(() => []);
          if (teams.length > 0) {
            setShareTeamGroups([{
              customerId: currentUser.customerId,
              customerName: '',
              teams,
            }]);
          }
        } catch { /**/ }
      }
    };

    loadProviders();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [wf, kbs] = await Promise.all([
        workflowApi.get(id!),
        knowledgeBaseApi.list({ includeSystem: true }), // 시스템 지식베이스도 포함
      ]);
      console.log('Loaded knowledge bases:', kbs);
      console.log('Loaded workflow:', wf);
      console.log('Loaded nodes:', wf.nodes);
      // LLM 노드 데이터 확인
      const llmNode = wf.nodes.find(n => n.type === 'llm');
      console.log('LLM node data:', llmNode?.data);
      setWorkflow(wf);
      
      // 워크플로우에 연결된 지식베이스(linkedKnowledgeBases)를 사용자 목록(kbs)에 병합
      // 사용자가 접근 불가한 지식베이스도 읽기 전용으로 표시
      const linkedKbs = (wf as any).linkedKnowledgeBases as Array<{ id: string; name: string }> | undefined;
      const mergedKbs = [...kbs];
      if (linkedKbs) {
        for (const linked of linkedKbs) {
          if (!mergedKbs.some(kb => kb.id === linked.id)) {
            // 사용자 목록에 없는 지식베이스 → 최소 정보로 추가 (읽기 전용 표시용)
            mergedKbs.push({
              id: linked.id,
              name: `${linked.name} (읽기 전용)`,
              // 필수 필드 기본값
              status: 'ready',
              documentCount: 0,
              chunkSize: 0,
              chunkOverlap: 0,
              sourceType: 'file',
              owner: '',
              createdAt: '',
              updatedAt: '',
            } as any);
          }
        }
      }
      setKnowledgeBases(mergedKbs);

      // 스케줄 워크플로우이면 활성 상태 조회
      const hasSchedule = wf.nodes.some(n => n.type === 'schedule-trigger');
      if (hasSchedule && wf.status === 'deployed') {
        try {
          const activeIds = await workflowApi.getActiveSchedules();
          setIsScheduleActive(activeIds.includes(wf.id));
        } catch { /* 무시 */ }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      showToast('error', '워크플로우를 불러오는데 실패했습니다.');
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  // 실행 로그 로드
  const loadLogs = async () => {
    if (!id || id === 'new') return;
    setLogsLoading(true);
    try {
      const logs = await workflowApi.getLogs(id, 20);
      // startedAt 기준 최신순 정렬
      setRunLogs(logs.sort((a, b) => b.startedAt.localeCompare(a.startedAt)));
    } catch {
      // 로그 조회 실패는 무시 (테이블 없는 초기 환경 등)
    } finally {
      setLogsLoading(false);
    }
  };

  // 실행 로그 탭 배지용 - 탭을 열기 전에도 실패 건수가 보이도록 페이지 진입 시 한 번 불러옴
  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 최근 로그(최대 20건) 중 실패 건수
  const failedLogCount = runLogs.filter((log) => log.status === 'failed').length;

  // 접힌 오른쪽 패널 펼치기 + 누른 탭으로 전환 (탭 전환은 기존 toggleLogPanel 그대로 사용)
  const openSidePanel = (tab: 'examples' | 'logs') => {
    setSidePanelCollapsed(false);
    if ((tab === 'logs') !== showLogPanel) toggleLogPanel();
  };

  // 로그 패널 열기/닫기
  const toggleLogPanel = () => {
    if (!showLogPanel) {
      loadLogs();
    }
    setShowLogPanel((v) => !v);
  };

  // 수동 실행
  const handleManualRun = () => {
    if (!id || !workflow) return;
    if (workflow.status !== 'deployed') {
      showToast('error', '배포된 워크플로우만 실행할 수 있습니다.');
      return;
    }
	setRunConfirmOpen(true);
	};
	
	const executeManualRun = async () => {
	if (!id) return;
	setRunConfirmOpen(false);
    setRunning(true);
    try {
      await workflowApi.run(id);
      showToast('success', '워크플로우 실행을 시작했습니다. 잠시 후 로그를 확인하세요.');
      setTimeout(() => loadLogs(), 3000);
    } catch {
      showToast('error', '워크플로우 실행에 실패했습니다.');
    } finally {
      setRunning(false);
    }
  };

  // 스케줄 정지
  const handleSchedulePause = async () => {
    if (!id || !workflow) return;
    setScheduleActing(true);
    try {
      await workflowApi.pauseSchedule(id);
      setIsScheduleActive(false);
      showToast('success', '스케줄이 정지됐습니다.');
    } catch {
      showToast('error', '정지에 실패했습니다.');
    } finally {
      setScheduleActing(false);
    }
  };

  // 스케줄 재가동
  const handleScheduleResume = async () => {
    if (!id || !workflow) return;
    setScheduleActing(true);
    try {
      await workflowApi.resumeSchedule(id);
      setIsScheduleActive(true);
      showToast('success', '스케줄이 재가동됐습니다.');
    } catch {
      showToast('error', '재가동에 실패했습니다.');
    } finally {
      setScheduleActing(false);
    }
  };

  // 캔버스 좌표 계산
  const getCanvasPoint = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  // 노드 크기 (실제 DOM 렌더링 기준)
  const getNodeSize = (node: WorkflowNode) => {
    // knowledge-retrieval만 아래에 드롭다운이 있어서 더 큼
    const hasConfigRow = node.type === 'knowledge-retrieval';
    // 노드 폭은 설정 여부와 관계없이 216 고정 (WorkflowBuilderPage.css .wnode width)
    const width = 180;
    // 일반 노드: border(2) + wnode-inner(64) = 66
    // knowledge-retrieval: 66 + config-row(select 30 + padding-bottom 14) = 110
    const height = hasConfigRow ? 110 : 66;
    return { width, height };
  };

  // 포트 위치 계산 (포트 중심점)
  const getPortPosition = useCallback((node: WorkflowNode, port: PortType) => {
    const size = getNodeSize(node);
    // portRadius = 5.5 (포트 크기 11px / 2) — 포트 중심 오프셋 참고용
    switch (port) {
      case 'in':
        // left: -6px, 포트 중심은 노드 왼쪽에서 -6 + 5.5 = -0.5 (약 0)
        return { x: node.position.x, y: node.position.y + size.height / 2 };
      case 'out':
        // right: -6px, 포트 중심은 노드 오른쪽에서 +6 - 5.5 = +0.5 (약 width)
        return { x: node.position.x + size.width, y: node.position.y + size.height / 2 };
      case 'top':
        // top: -6px, left: 50%, 포트 중심
        return { x: node.position.x + size.width / 2, y: node.position.y };
      case 'bottom':
        // bottom: -6px, left: 50%, 포트 중심
        return { x: node.position.x + size.width / 2, y: node.position.y + size.height };
      default:
        return { x: node.position.x, y: node.position.y };
    }
  }, []);

  // 베지어 곡선 경로 생성
  const getBezierPath = useCallback((
    p1: { x: number; y: number },
    p2: { x: number; y: number },
    fromPort: PortType,
    toPort: PortType
  ) => {
    const dist = Math.max(Math.abs(p2.x - p1.x), Math.abs(p2.y - p1.y)) * 0.5;
    const offset = Math.max(dist, 40);

    let c1x = p1.x, c1y = p1.y, c2x = p2.x, c2y = p2.y;

    // 출발점 컨트롤 포인트
    if (fromPort === 'out') c1x += offset;
    else if (fromPort === 'in') c1x -= offset;
    else if (fromPort === 'bottom') c1y += offset;
    else if (fromPort === 'top') c1y -= offset;

    // 도착점 컨트롤 포인트
    if (toPort === 'in') c2x -= offset;
    else if (toPort === 'out') c2x += offset;
    else if (toPort === 'top') c2y -= offset;
    else if (toPort === 'bottom') c2y += offset;

    return `M ${p1.x} ${p1.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }, []);

  // 팔레트 아이템 드래그 시작
  const handlePaletteDragStart = (e: React.DragEvent, nodeType: NodeTypeInfo) => {
    setDraggedPaletteItem(nodeType);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', nodeType.type);
  };

  // 팔레트 아이템 드래그 종료
  const handlePaletteDragEnd = () => {
    setDraggedPaletteItem(null);
  };

  // 캔버스 드래그 오버
  const handleCanvasDragOver = (e: React.DragEvent) => {
    if (!draggedPaletteItem) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // 캔버스 드롭 (노드 추가)
  const handleCanvasDrop = (e: React.DragEvent) => {
    if (!draggedPaletteItem || !workflow) return;
    e.preventDefault();

    const point = getCanvasPoint(e);
    
    // LLM 노드 기본값: Nova Lite 계열 우선, 없으면 첫 번째 모델
    let llmDefaultData: Record<string, unknown> = { systemPrompt: DEFAULT_LLM_SYSTEM_PROMPT };
    if (draggedPaletteItem.type === 'llm' && bedrockModels.length > 0) {
      // Nova Lite 계열 모델 찾기 (가성비 좋은 모델)
      const novaLiteModel = bedrockModels.find(m => 
        m.modelId.toLowerCase().includes('nova-lite') || 
        m.modelId.toLowerCase().includes('nova-micro')
      );
      const defaultModel = novaLiteModel || bedrockModels[0];
      llmDefaultData = {
        ...llmDefaultData,
        provider: 'bedrock',
        models: [defaultModel.modelId],
        temperature: '0.5',
      };
    }
    
    const newNode: WorkflowNode = {
      id: `node-${Date.now()}`,
      type: draggedPaletteItem.type,
      position: { x: Math.max(0, point.x - 75), y: Math.max(0, point.y - 19) },
      data: draggedPaletteItem.type === 'llm' ? llmDefaultData : {},
    };

    setWorkflow({
      ...workflow,
      nodes: [...workflow.nodes, newNode],
    });

    setDraggedPaletteItem(null);
  };

  // 노드 드래그 시작
  const handleNodeMouseDown = (e: React.MouseEvent, nodeId: string) => {
    if ((e.target as HTMLElement).closest('.wnode-del, .port, select, input, button')) return;
    e.preventDefault();

    const node = workflow?.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    const point = getCanvasPoint(e);
    setDraggingNodeId(nodeId);
    setDragOffset({
      x: point.x - node.position.x,
      y: point.y - node.position.y,
    });
  };

  // 포트에서 선 그리기 시작
  const handlePortMouseDown = (e: React.MouseEvent, nodeId: string, port: PortType) => {
    // out, bottom, top 포트에서만 선 그리기 시작
    if (port === 'in') return;
    e.preventDefault();
    e.stopPropagation();

    const point = getCanvasPoint(e);
    setConnectingLine({
      fromNodeId: nodeId,
      fromPort: port,
      toX: point.x,
      toY: point.y,
    });
  };

  // 마우스 이동 핸들러
  useEffect(() => {
    if (!draggingNodeId && !connectingLine) return;

    const handleMouseMove = (e: MouseEvent) => {
      const point = getCanvasPoint(e);

      if (draggingNodeId && workflow) {
        setWorkflow({
          ...workflow,
          nodes: workflow.nodes.map((n) =>
            n.id === draggingNodeId
              ? { ...n, position: { x: Math.max(0, point.x - dragOffset.x), y: Math.max(0, point.y - dragOffset.y) } }
              : n
          ),
        });
      }

      if (connectingLine) {
        setConnectingLine({
          ...connectingLine,
          toX: point.x,
          toY: point.y,
        });
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (connectingLine && workflow) {
        // 연결 대상 포트 찾기
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const portEl = target?.closest('.port') as HTMLElement;
        const nodeEl = target?.closest('.wnode') as HTMLElement;

        if (portEl && nodeEl) {
          const toNodeId = nodeEl.dataset.id;
          const toPort = portEl.dataset.port as PortType;

          if (toNodeId && toNodeId !== connectingLine.fromNodeId && (toPort === 'in' || toPort === 'top' || toPort === 'bottom')) {
            // 중복 엣지 확인
            const isDuplicate = workflow.edges.some(
              (ed) =>
                ed.source === connectingLine.fromNodeId &&
                ed.target === toNodeId &&
                ed.sourceHandle === connectingLine.fromPort &&
                ed.targetHandle === toPort
            );

            if (!isDuplicate) {
              const newEdge: WorkflowEdge = {
                id: `edge-${Date.now()}`,
                source: connectingLine.fromNodeId,
                target: toNodeId,
                sourceHandle: connectingLine.fromPort,
                targetHandle: toPort,
              };

              setWorkflow({
                ...workflow,
                edges: [...workflow.edges, newEdge],
              });
            }
          }
        } else if (nodeEl && !portEl) {
          // 노드에 직접 연결 (기본 포트)
          const toNodeId = nodeEl.dataset.id;
          if (toNodeId && toNodeId !== connectingLine.fromNodeId) {
            const toPort: PortType = connectingLine.fromPort === 'bottom' ? 'top' : connectingLine.fromPort === 'top' ? 'bottom' : 'in';

            const isDuplicate = workflow.edges.some(
              (ed) =>
                ed.source === connectingLine.fromNodeId &&
                ed.target === toNodeId
            );

            if (!isDuplicate) {
              const newEdge: WorkflowEdge = {
                id: `edge-${Date.now()}`,
                source: connectingLine.fromNodeId,
                target: toNodeId,
                sourceHandle: connectingLine.fromPort,
                targetHandle: toPort,
              };

              setWorkflow({
                ...workflow,
                edges: [...workflow.edges, newEdge],
              });
            }
          }
        }
      }

      setDraggingNodeId(null);
      setConnectingLine(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingNodeId, connectingLine, workflow, dragOffset, getCanvasPoint]);

  // 노드 삭제
  const deleteNode = (nodeId: string) => {
    if (!workflow) return;
    setWorkflow({
      ...workflow,
      nodes: workflow.nodes.filter((n) => n.id !== nodeId),
      edges: workflow.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
    });
  };

  // 엣지 삭제
  const deleteEdge = (edgeId: string) => {
    if (!workflow) return;
    setWorkflow({
      ...workflow,
      edges: workflow.edges.filter((e) => e.id !== edgeId),
    });
  };

  // 노드 데이터 업데이트
  const updateNodeData = (nodeId: string, data: Record<string, unknown>) => {
    if (!workflow) return;
    setWorkflow({
      ...workflow,
      nodes: workflow.nodes.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n
      ),
    });
  };

  // 설정 모달 열기 — 로컬 상태 그대로 사용 (서버 재조회 시 미저장 노드가 사라지는 버그 방지)
  const openConfigModal = (node: WorkflowNode) => {
    if (!workflow) return;
    // 로컬 workflow에서 최신 노드 데이터 사용
    const currentNode = workflow.nodes.find((n) => n.id === node.id) || node;
    setConfigModalNode(currentNode);
    // LLM 노드이고 systemPrompt가 비어있으면 기본값 자동 세팅
    const nodeData = { ...(currentNode.data || {}) };
    if (currentNode.type === 'llm' && !nodeData.systemPrompt) {
      nodeData.systemPrompt = DEFAULT_LLM_SYSTEM_PROMPT;
    }
    setTempNodeConfig(nodeData);

    // LLM 노드 + needsTeamSelect 인 경우: 저장된 teamId로 모델 목록 자동 복원
    if (currentNode.type === 'llm') {
      const savedTeamId = nodeData.teamId as string | undefined;
      const currentUser = authApi.getUser();
      const role = currentUser?.role || '';
      const needsTeamSelect = ['tenantAdmin', 'customerAdmin', 'productAdmin', 'admin'].includes(role);
      if (needsTeamSelect && savedTeamId) {
        // 고객사는 자동 복원 안 함 (사용자가 필요시 선택)
        setLlmCustomerId((nodeData.customerId as string) || '');
        setLlmTeamModelsLoading(true);
        adminApi.listBedrockModels(`team:${savedTeamId}`)
          .then(models => {
            setBedrockModels(models);
            if (models.length > 0) {
              setConnectedProviders(prev =>
                prev.some(p => p.id === 'bedrock') ? prev : [...prev, { id: 'bedrock', name: 'Amazon Bedrock' }]
              );
            }
          })
          .catch(() => {})
          .finally(() => setLlmTeamModelsLoading(false));
      } else {
        setLlmCustomerId('');
      }
    }

    setConfigModalPos({ x: 0, y: 0 });
  };

  // 설정 모달 닫기
  const closeConfigModal = () => {
    setConfigModalNode(null);
	setConfigModalShowErrors(false);
    setTempNodeConfig({});
  };

  // 설정 저장 (로컬 + 백엔드 자동 저장)
  const saveNodeConfig = async () => {
    if (!configModalNode || !workflow) return;

    // Google Drive 업로드 노드 - 저장 시 실제 파일 업로드 실행
    const cfg = tempNodeConfig as { operation?: string; uploadFile?: File; folderId?: string; folderPath?: string; uploadFileName?: string };
    if (configModalNode.type === 'google-drive' && cfg.operation === 'upload' && cfg.uploadFile) {
      try {
        const result = await connectorApi.uploadToGoogleDrive(cfg.uploadFile, cfg.folderId);
        showToast('success', `Drive 업로드 완료: ${result.name}`);
        const { uploadFile: _, ...configWithoutFile } = cfg;
        (tempNodeConfig as Record<string, unknown>).uploadFile = undefined;
        (tempNodeConfig as Record<string, unknown>).uploadFileName = configWithoutFile.uploadFileName;
      } catch {
        showToast('error', 'Drive 업로드에 실패했습니다.');
        return;
      }
    }

    // Dropbox 업로드 노드 - 저장 시 실제 파일 업로드 실행
    if (configModalNode.type === 'dropbox' && cfg.operation === 'upload' && cfg.uploadFile) {
      try {
        const result = await connectorApi.uploadToDropbox(cfg.uploadFile, cfg.folderPath);
        showToast('success', `Dropbox 업로드 완료: ${result.name}`);
        const { uploadFile: _, ...configWithoutFile } = cfg;
        (tempNodeConfig as Record<string, unknown>).uploadFile = undefined;
        (tempNodeConfig as Record<string, unknown>).uploadFileName = configWithoutFile.uploadFileName;
      } catch {
        showToast('error', 'Dropbox 업로드에 실패했습니다.');
        return;
      }
    }
	
	// 이메일 커넥터 노드 필수 필드 검증
	if (configModalNode.type === 'email') {
		const ec = tempNodeConfig as { connectorId?: string; operation?: string; to?: string; subject?: string };
		if (!ec.connectorId) {
			setConfigModalShowErrors(true);
			showToast('error', '이메일 커넥터를 선택해주세요.');
			return;
		}
		if ((ec.operation === 'send' || !ec.operation) && !ec.to?.trim()) {
			setConfigModalShowErrors(true);
			showToast('error', '수신자를 입력해주세요.');
			return;
		}
	}

	// Notion 노드 필수 필드 검증
	if (configModalNode.type === 'notion') {
		const nc = tempNodeConfig as { connected?: boolean; operation?: string; databaseId?: string; pageId?: string };
		if (!nc.connected) {
			showToast('error', 'Notion 워크스페이스에 먼저 연결해주세요.');
			return;
		}
		const op = nc.operation || 'watch_db';
		if ((op === 'watch_db' || op === 'add_db_item') && !nc.databaseId?.trim()) {
			setConfigModalShowErrors(true);
			showToast('error', '데이터베이스 ID를 입력해주세요.');
			return;
		}
		if ((op === 'watch_page' || op === 'create_page' || op === 'read_page') && !nc.pageId?.trim()) {
			setConfigModalShowErrors(true);
			showToast('error', '페이지 ID를 입력해주세요.');
			return;
		}
	}

    // 로컬 상태 업데이트
    const updatedNodes = workflow.nodes.map((n) =>
      n.id === configModalNode.id ? { ...n, data: { ...n.data, ...tempNodeConfig } } : n
    );
    
    const updatedWorkflow = { ...workflow, nodes: updatedNodes };
    setWorkflow(updatedWorkflow);
    closeConfigModal();

    // 새 워크플로우: 아직 DB에 없으므로 로컬에만 반영. 상단 '저장' 시 함께 저장됨
    if (workflow.id === 'new') {
      showToast('success', '노드 설정이 적용되었습니다. 상단 저장을 누르면 저장됩니다.');
      return;
    }
    // 트리거 노드가 하나는 있어야 저장 가능
    const TRIGGER_NODE_TYPES_CHECK = ['trigger', 'schedule-trigger', 'manual-trigger'];
    const hasTriggerNode = updatedNodes.some(n => TRIGGER_NODE_TYPES_CHECK.includes(n.type));
    if (!hasTriggerNode) {
      showToast('error', '트리거 노드를 하나 이상 추가해주세요.');
      return;
    }
    try {
      await workflowApi.update(workflow.id, {
        nodes: updatedNodes,
        edges: workflow.edges,
        knowledgeBaseIds: workflow.knowledgeBaseIds,
      });
      showToast('success', '노드 설정이 저장되었습니다.');
    } catch (error) {
      showToast('error', '설정 저장에 실패했습니다.');
    }
  };

  // 스케줄 트리거 포함 여부
  const hasScheduleTrigger = !!workflow?.nodes.some(n => n.type === 'schedule-trigger');
  // 실행 로그가 필요한 트리거 (스케줄 또는 수동 실행) 포함 여부
  const hasRunTrigger = !!workflow?.nodes.some(n => n.type === 'schedule-trigger' || n.type === 'manual-trigger');

  // 저장 버튼 클릭 - 이름이 비었거나 스케줄 워크플로우면 저장 모달, 아니면 바로 저장
  const handleSave = async () => {
    if (!workflow) return;
    // 트리거 노드가 하나는 있어야 저장 가능
    const TRIGGER_NODE_TYPES = ['trigger', 'schedule-trigger', 'manual-trigger'];
    const hasTrigger = workflow.nodes.some(n => TRIGGER_NODE_TYPES.includes(n.type));
    if (!hasTrigger) {
      showToast('error', '트리거 노드를 하나 이상 추가해주세요.');
      return;
    }
    // 채팅 트리거가 있으면 AI 응답(LLM) 노드도 필수
    const hasChatTrigger = workflow.nodes.some(n => n.type === 'trigger');
    const hasLlmNode = workflow.nodes.some(n => n.type === 'llm');
    if (hasChatTrigger && !hasLlmNode) {
      showToast('error', '채팅 워크플로우에는 AI 응답 노드가 필요합니다. 팔레트에서 AI 응답 노드를 추가해주세요.');
      return;
    }
    // AI 응답(LLM) 노드가 있으면 채팅 시작 트리거 필요
    if (hasLlmNode && !hasChatTrigger) {
      showToast('error', 'AI 응답 노드는 채팅 시작 트리거와 함께 사용해야 합니다.');
      return;
    }
	// AI 응답(LLM) 노드 검증
	const llmNode = workflow.nodes.find(n => n.type === 'llm');
	if (llmNode) {
	  const llmTeamId = llmNode.data?.teamId as string;
	  const currentUserV = authApi.getUser();
	  const currentRoleV = currentUserV?.role || '';
	  const needsTeamSelectV = ['tenantAdmin', 'customerAdmin', 'productAdmin', 'admin'].includes(currentRoleV);
	  // tenantAdmin/customerAdmin/productAdmin은 팀(Bedrock 계정) 선택 필수
	  if (needsTeamSelectV && (!llmTeamId || llmTeamId.trim() === '')) {
	    showToast('error', 'AI 응답 노드에서 Bedrock 계정이 연결된 팀을 선택해야 합니다. AI 응답 설정(⚙️)을 열어 팀을 선택해주세요.');
	    return;
	  }
	  // 모델 미설정은 저장 차단 대신 경고만
	  const llmModelsV = llmNode.data?.models as string[] | undefined;
	  const primaryModelV = llmModelsV?.[0] || (llmNode.data?.model as string);
	  if (!primaryModelV || primaryModelV.trim() === '') {
		showToast('info', 'AI 응답 노드에서 모델을 선택하고 저장하세요. 지금은 기본 모델로 배포됩니다.');
      }
    }
    const needName = !workflow.name || workflow.name.trim() === '' || workflow.name === '새 워크플로우';
    if (needName || hasScheduleTrigger) {
      // 저장 모달에서 이름 입력 + (스케줄이면) 실행 여부 선택
      setSaveName(needName ? '' : workflow.name);
      setSaveDescription(workflow.description || '');
      setSaveScheduleActive(isScheduleActive || workflow.id === 'new'); // 기본값: 현재 활성 상태(신규는 실행)
      setShowSaveConfirm(true);
      return;
    }
    await doSave();
  };

  // 실제 저장 수행 (scheduleActive: 스케줄 워크플로우일 때 저장 후 실행 여부)
  const doSave = async (scheduleActive?: boolean) => {
    if (!workflow) return;

    // ── 연결 검증: 노드 2개 이상일 때 고립된 노드 있으면 저장 차단 ──
    if (workflow.nodes.length > 1) {
      // 트리거 노드에서 BFS로 도달 가능한 노드 집합 계산
      const TRIGGER_TYPES = ['trigger', 'chat_start', 'manual_trigger', 'schedule', 'schedule-trigger', 'webhook', 'mail-trigger', 'webhook-trigger'];
      const triggerNode = workflow.nodes.find(n => TRIGGER_TYPES.includes(n.type));
      if (triggerNode) {
        const reachable = new Set<string>();
        const queue = [triggerNode.id];
        while (queue.length > 0) {
          const current = queue.shift()!;
          reachable.add(current);
          workflow.edges.filter(e => e.source === current).forEach(e => {
            if (!reachable.has(e.target)) queue.push(e.target);
          });
        }
        const unreachable = workflow.nodes.filter(n => !reachable.has(n.id));
        if (unreachable.length > 0) {
          const names = unreachable.map(n => (n.data as any)?.label || n.type).join(', ');
          showToast('error', `트리거에서 연결되지 않은 노드가 있습니다: ${names}. 삭제하거나 연결해주세요.`);
          setSaving(false);
          return;
        }
      } else {
        // 트리거 없으면 기존 방식으로 고립 노드 체크
        const connectedNodeIds = new Set<string>();
        workflow.edges.forEach(e => {
          connectedNodeIds.add(e.source);
          connectedNodeIds.add(e.target);
        });
        const isolatedNodes = workflow.nodes.filter(n => !connectedNodeIds.has(n.id));
        if (isolatedNodes.length > 0) {
          const names = isolatedNodes.map(n => (n.data as any)?.label || n.type).join(', ');
          showToast('error', `연결되지 않은 노드가 있습니다: ${names}. 화살표로 노드를 연결한 후 저장하세요.`);
          setSaving(false);
          return;
        }
      }
    }
    const finalName = (saveName.trim() || workflow.name || '').trim();
    const finalDescription = (saveDescription || workflow.description || '').trim();

    try {
      setSaving(true);

      let savedId = workflow.id;

      if (workflow.id === 'new') {
        // 처음 저장: DB에 생성 (바로 deployed로 저장)
        const created = await workflowApi.create({
          name: finalName,
          description: finalDescription,
          nodes: workflow.nodes,
          edges: workflow.edges,
          knowledgeBaseIds: workflow.knowledgeBaseIds,
          status: 'deployed',
        } as any);
        savedId = created.id;
        // 새 워크플로우는 draft로 저장 (명시적 배포 필요)
        const saved = created;
        // URL 교체 후 loadData 재조회 생략 (노드 유실 방지)
        justCreatedRef.current = created.id;
        navigate(`/workflows/${created.id}`, { replace: true });
        setWorkflow(saved);
      } else {
        await workflowApi.update(workflow.id, {
          name: finalName,
          description: finalDescription,
          nodes: workflow.nodes,
          edges: workflow.edges,
          knowledgeBaseIds: workflow.knowledgeBaseIds,
          status: 'deployed',
        });
        setWorkflow((prev) => (prev ? { ...prev, name: finalName, description: finalDescription } : prev));
        // 저장은 저장만 (배포는 별도 버튼으로)
      }

      // 스케줄 워크플로우: 저장 시 선택한 실행/미실행 반영
      if (hasScheduleTrigger && scheduleActive !== undefined) {
        try {
          if (scheduleActive) {
            await workflowApi.resumeSchedule(savedId);
            setIsScheduleActive(true);
          } else {
            await workflowApi.pauseSchedule(savedId);
            setIsScheduleActive(false);
          }
        } catch { /* 스케줄 상태 반영 실패는 무시 */ }
      }

      showToast('success', '저장되었습니다.');
    } catch (error) {
      showToast('error', '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 테마 설정 모달 열기
  const openThemeModal = () => {
    setTempTheme(workflow?.theme || {});
    setThemeModalPos({ x: 0, y: 0 });
    setShowThemeModal(true);
  };

  // 테마 설정 모달 닫기
  const closeThemeModal = () => {
    setShowThemeModal(false);
    setTempTheme({});
  };

  // 워크플로우 정보 수정 모달 열기
  const openInfoModal = () => {
    setTempWorkflowInfo({
      name: workflow?.name || '',
      description: workflow?.description || '',
    });
    setInfoModalPos({ x: 0, y: 0 });
    setShowInfoModal(true);
  };

  // 정보 팝오버 닫기 (바깥 클릭 · ESC 감지는 Popover 컴포넌트에서)
  const closeInfoPopover = useCallback(() => setShowInfoPopover(false), []);

  // 워크플로우 ID 복사
  const copyWorkflowId = async () => {
    if (!workflow) return;
    try {
      await navigator.clipboard.writeText(workflow.id);
      showToast('success', '워크플로우 ID가 복사되었습니다.');
    } catch {
      showToast('error', '복사에 실패했습니다.');
    }
  };

  // 워크플로우 정보 수정 모달 닫기
  const closeInfoModal = () => {
    setShowInfoModal(false);
    setTempWorkflowInfo({ name: '', description: '' });
  };

  // 모달 오버레이 마우스다운 핸들러 (텍스트 선택 시 닫히지 않도록)
  const handleOverlayMouseDown = (e: React.MouseEvent, closeFn: () => void) => {
    if (e.target === e.currentTarget) closeFn();
  };

  // 워크플로우 정보 저장
  const saveWorkflowInfo = async () => {
    if (!workflow || !tempWorkflowInfo.name.trim()) return;
    
    setSavingInfo(true);
    try {
      if (workflow.id === 'new') {
        // 새 워크플로우: 정보 입력 시 DB에 생성 후 URL 교체
        const created = await workflowApi.create({
          name: tempWorkflowInfo.name.trim(),
          description: tempWorkflowInfo.description.trim(),
          nodes: workflow.nodes,
          edges: workflow.edges,
          knowledgeBaseIds: workflow.knowledgeBaseIds,
        } as any);
        navigate(`/workflows/${created.id}`, { replace: true });
        setWorkflow(created);
        closeInfoModal();
        showToast('success', '워크플로우가 생성되었습니다.');
        return;
      }
      await workflowApi.update(workflow.id, {
        name: tempWorkflowInfo.name.trim(),
        description: tempWorkflowInfo.description.trim(),
      });
      const updated = await workflowApi.get(workflow.id);
      setWorkflow(updated);
      closeInfoModal();
      showToast('success', '워크플로우 정보가 저장되었습니다.');
    } catch (error) {
      showToast('error', '저장에 실패했습니다.');
    } finally {
      setSavingInfo(false);
    }
  };

  // 상단 제목 · 설명 인라인 편집 시작
  const inlineCancelRef = useRef(false);
  const startInlineEdit = (field: 'name' | 'description') => {
    if (!workflow || !canEdit) return;
    inlineCancelRef.current = false;
    setInlineDraft(field === 'name' && workflow.name === '새 워크플로우' ? '' : (workflow[field] || ''));
    setInlineEdit(field);
  };

  // 인라인 편집 자동 저장 (blur 시) - 노드 등 미저장 변경을 덮지 않도록 해당 필드만 로컬 반영
  const commitInlineEdit = async () => {
    const field = inlineEdit;
    setInlineEdit(null);
    if (!workflow || !field || inlineCancelRef.current) return;
    const value = inlineDraft.trim();
    if (field === 'name' && !value) return;
    if (value === (workflow[field] || '')) return;

    const prevValue = workflow[field];
    setWorkflow((prev) => (prev ? { ...prev, [field]: value } : prev));
    if (workflow.id === 'new') return; // 새 워크플로우는 저장 버튼으로만
    try {
      await workflowApi.update(workflow.id, field === 'name' ? { name: value } : { description: value });
    } catch {
      setWorkflow((prev) => (prev ? { ...prev, [field]: prevValue } : prev));
      showToast('error', '저장에 실패했습니다.');
    }
  };

  const handleInlineKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return; // 한글 조합 중 Enter 무시
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      inlineCancelRef.current = true;
      e.currentTarget.blur();
    }
  };

  // 모달 드래그 핸들러
  const handleModalDragStart = (e: React.MouseEvent, modalType: 'theme' | 'info' | 'config') => {
    setIsDraggingModal(modalType);
    const pos = modalType === 'theme' ? themeModalPos : modalType === 'info' ? infoModalPos : configModalPos;
    setModalDragStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  // 모달 드래그 이벤트
  useEffect(() => {
    if (!isDraggingModal) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newPos = {
        x: e.clientX - modalDragStart.x,
        y: e.clientY - modalDragStart.y,
      };
      if (isDraggingModal === 'theme') setThemeModalPos(newPos);
      else if (isDraggingModal === 'info') setInfoModalPos(newPos);
      else if (isDraggingModal === 'config') setConfigModalPos(newPos);
    };

    const handleMouseUp = () => setIsDraggingModal(null);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingModal, modalDragStart]);

  // 테마 저장
  const saveTheme = async () => {
    if (!workflow) return;
    if (workflow.id === 'new') {
      showToast('info', '먼저 워크플로우를 저장해주세요.');
      return;
    }
    
    setSavingTheme(true);
    
    // 빈 quickPrompts 항목 필터링
    const cleanedTheme = {
      ...tempTheme,
      quickPrompts: (tempTheme.quickPrompts || []).filter(p => p.trim()),
    };
    
    try {
      await workflowApi.update(workflow.id, {
        theme: cleanedTheme,
      });
      // 워크플로우 다시 로드하여 최신 데이터 반영
      const updated = await workflowApi.get(workflow.id);
      setWorkflow(updated);
      closeThemeModal();
      showToast('success', '테마 설정이 저장되었습니다.');
    } catch (error) {
      showToast('error', '테마 저장에 실패했습니다.');
    } finally {
      setSavingTheme(false);
    }
  };

  // 공유하기 모달 - 공유 대상(챗봇 · 템플릿) 선택
  // 챗봇은 "채팅 시작" 트리거(type: 'trigger')가 있는 대화형 워크플로우만
  const canShareChatbot = !!workflow && workflow.nodes.some(n => n.type === 'trigger');
  const allShared = !!workflow && workflow.isSharedAsTemplate === true && (!canShareChatbot || workflow.isSharedAsChatbot === true);
  const [shareOpen, setShareOpen] = useState(false);
  // 챗봇/템플릿 섹션별 독립 팀 목록 (새 데이터 모델)
  const [chatbotTeamIds, setChatbotTeamIds] = useState<string[]>([]);
  const [templateTeamIds, setTemplateTeamIds] = useState<string[]>([]);
  const [chatbotAddTeam, setChatbotAddTeam] = useState<string>('');
  const [templateAddTeam, setTemplateAddTeam] = useState<string>('');
  const [shareCustomerId, setShareCustomerId] = useState<string>(''); // 공유 고객사 (productAdmin/tenantAdmin)
  const [llmCustomerId, setLlmCustomerId] = useState<string>(''); // LLM 모달 고객사 (productAdmin/tenantAdmin)

  // shareTeamGroups 로드 완료 후 고객사 자동 복원
  useEffect(() => {
    if (!shareOpen || shareTeamGroups.length === 0) return;
    const allIds = [...chatbotTeamIds, ...templateTeamIds];
    if (allIds.length > 0 && !shareCustomerId) {
      const group = shareTeamGroups.find(g => g.teams.some(t => allIds.includes(t.id)));
      if (group) setShareCustomerId(group.customerId);
    }
  }, [shareTeamGroups, shareOpen]);

  const openShareModal = () => {
    if (!workflow) return;
    const cu = authApi.getUser();
    const role = cu?.role || '';
    if (['teamAdmin', 'user'].includes(role) && cu?.teamId) {
      const myTeamId = cu.teamId;
      // 자기 팀이 각 공유 목록에 포함돼 있으면 공유 중
      setChatbotTeamIds(workflow.sharedChatbotTeamIds?.includes(myTeamId) ? [myTeamId] : []);
      setTemplateTeamIds(workflow.sharedTemplateTeamIds?.includes(myTeamId) ? [myTeamId] : []);
      setShareCustomerId('');
    } else {
      // 저장된 값 복원 — 레거시 sharedTeamIds 하위호환
      const legacyCbIds = workflow.isSharedAsChatbot ? (workflow.sharedTeamIds || []) : [];
      const legacyTplIds = workflow.isSharedAsTemplate ? (workflow.sharedTeamIds || []) : [];
      setChatbotTeamIds(workflow.sharedChatbotTeamIds ?? legacyCbIds);
      setTemplateTeamIds(workflow.sharedTemplateTeamIds ?? legacyTplIds);
      setShareCustomerId('');
    }
    setChatbotAddTeam('');
    setTemplateAddTeam('');
    setShareOpen(true);
  };

  // 챗봇·템플릿 공유 설정 한 번에 저장
  const handleShareSave = async () => {
    if (!workflow) return;
    try {
      setSaving(true);
      const patch = {
        sharedChatbotTeamIds: chatbotTeamIds,
        sharedTemplateTeamIds: templateTeamIds,
        isSharedAsChatbot: chatbotTeamIds.length > 0,
        isSharedAsTemplate: templateTeamIds.length > 0,
      };
      await workflowApi.update(workflow.id, patch);
      setWorkflow({ ...workflow, ...patch });
      showToast('success', '공유 설정이 저장되었습니다.');
      setShareOpen(false);
    } catch {
      showToast('error', '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 예시 워크플로우 불러오기 전 필요 노드 설치 여부 확인
  const checkRequiredNodes = (requiredNodeIds: string[]): string[] => {
    const installedNodes = getInstalledNodes();
    const installedIds = installedNodes.map(n => n.id);
    return requiredNodeIds.filter(id => !installedIds.includes(id));
  };

  // 미설치 노드 ID → 한글 이름 매핑
  const getNodeName = (nodeId: string): string => {
    const names: Record<string, string> = {
      'chat_start': '채팅 시작',
      'ai': 'AI 응답',
      'search': '문서 검색',
      'cond': '판단',
      'schedule': '스케줄',
      'email': '이메일 커넥터',
      'ddg': '웹 검색',
      'slack': 'Slack',
      'notion': 'Notion',
      'drive': 'Google Drive',
    };
    return names[nodeId] || nodeId;
  };

  // RAG 예시 불러오기
  const loadRagExample = () => {
    if (!workflow) return;

    // 필요 노드 설치 여부 확인
    const missingNodes = checkRequiredNodes(EXAMPLE_RAG_REQUIRED_NODES);
    if (missingNodes.length > 0) {
      const nodeNames = missingNodes.map(getNodeName).join(', ');
      showToast('error', `마켓플레이스에서 다음 노드를 먼저 설치하세요: ${nodeNames}`);
      return;
    }

    setExampleConfirm('rag');
  };

  // DB 조회 자동 보고 예시
  const loadDbReportExample = () => {
    if (!workflow) return;

    // 필요 노드 설치 여부 확인
    const missingNodes = checkRequiredNodes(EXAMPLE_DB_REPORT_REQUIRED_NODES);
    if (missingNodes.length > 0) {
      const nodeNames = missingNodes.map(getNodeName).join(', ');
      showToast('error', `마켓플레이스에서 다음 노드를 먼저 설치하세요: ${nodeNames}`);
      return;
    }

    setExampleConfirm('db');
  };

  // Slack 알림 예시
  const loadSlackAlertExample = () => {
    if (!workflow) return;

    // 필요 노드 설치 여부 확인
    const missingNodes = checkRequiredNodes(EXAMPLE_SLACK_ALERT_REQUIRED_NODES);
    if (missingNodes.length > 0) {
      const nodeNames = missingNodes.map(getNodeName).join(', ');
      showToast('error', `마켓플레이스에서 다음 노드를 먼저 설치하세요: ${nodeNames}`);
      return;
    }

    setExampleConfirm('slack');
  };

  // 예시 불러오기 확인
  const confirmLoadExample = () => {
    if (!workflow || !exampleConfirm) return;
	
	// 현재 연결된 기본 provider/model 선택
	const defaultProvider = connectedProviders[0]?.id || 'bedrock';
	const defaultModel = defaultProvider === 'google'
	? (googleModels[0]?.modelId || '')
	: (bedrockModels[0]?.modelId || 'apac.amazon.nova-lite-v1:0');

    if (exampleConfirm === 'rag') {
      setWorkflow({
        ...workflow,
        nodes: [
          { id: 'ex-1', type: 'trigger',             position: { x: 80,  y: 180 }, data: {} },
          { id: 'ex-2', type: 'knowledge-retrieval', position: { x: 280, y: 168 }, data: {} },
          { id: 'ex-3', type: 'llm',                 position: { x: 490, y: 168 }, data: { systemPrompt: DEFAULT_LLM_SYSTEM_PROMPT, provider: defaultProvider, models: defaultModel ? [defaultModel] : [], temperature: '0.5' } },
        ],
        edges: [
          { id: 'ee-1', source: 'ex-1', target: 'ex-2', sourceHandle: 'out', targetHandle: 'in' },
          { id: 'ee-2', source: 'ex-2', target: 'ex-3', sourceHandle: 'out', targetHandle: 'in' },
        ],
      });
      showToast('success', 'RAG 챗봇 예시를 불러왔습니다. 문서 검색 노드에서 지식베이스를 선택하세요.');
    } else if (exampleConfirm === 'db') {
      setWorkflow({
        ...workflow,
        nodes: [
          { id: 'ex-1', type: 'schedule-trigger',    position: { x: 80,  y: 180 }, data: { scheduleType: 'daily', dailyHour: 9, dailyMinute: 0 } },
          { id: 'ex-2', type: 'knowledge-retrieval', position: { x: 290, y: 168 }, data: { fixedQuery: '오늘까지 매출 합계를 알려줘', outputFormat: 'html_table' } },
          { id: 'ex-3', type: 'email',               position: { x: 530, y: 168 }, data: { operation: 'send', subject: '[자동 보고] 일일 매출 현황' } },
        ],
        edges: [
          { id: 'ee-1', source: 'ex-1', target: 'ex-2', sourceHandle: 'out', targetHandle: 'in' },
          { id: 'ee-2', source: 'ex-2', target: 'ex-3', sourceHandle: 'out', targetHandle: 'in' },
        ],
      });
      showToast('success', 'DB 조회 자동 보고 예시를 불러왔습니다. 문서 검색 노드에서 지식베이스를, 이메일 노드에서 수신자를 설정하세요.');
    } else if (exampleConfirm === 'slack') {
      setWorkflow({
        ...workflow,
        nodes: [
          { id: 'ex-1', type: 'trigger',    position: { x: 80,  y: 180 }, data: {} },
          { id: 'ex-2', type: 'web-search', position: { x: 280, y: 168 }, data: {} },
          { id: 'ex-3', type: 'slack',      position: { x: 480, y: 168 }, data: { operation: 'send' } },
        ],
        edges: [
          { id: 'ee-1', source: 'ex-1', target: 'ex-2', sourceHandle: 'out', targetHandle: 'in' },
          { id: 'ee-2', source: 'ex-2', target: 'ex-3', sourceHandle: 'out', targetHandle: 'in' },
        ],
      });
      showToast('success', '웹 검색 → Slack 알림 예시를 불러왔습니다. Slack 노드에서 채널을 설정하세요.');
    }

    setExampleConfirm(null);
  };

  // 엣지 중간점 계산 (삭제 버튼 위치)
  const getEdgeMidpoint = (edge: WorkflowEdge) => {
    const fromNode = workflow?.nodes.find((n) => n.id === edge.source);
    const toNode = workflow?.nodes.find((n) => n.id === edge.target);
    if (!fromNode || !toNode) return { x: 0, y: 0 };

    const p1 = getPortPosition(fromNode, (edge.sourceHandle || 'out') as PortType);
    const p2 = getPortPosition(toNode, (edge.targetHandle || 'in') as PortType);

    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
  };

  if (loading) {
    return <div className="loading">로딩 중...</div>;
  }

  if (!workflow) {
    return <div className="empty-state">워크플로우를 찾을 수 없습니다.</div>;
  }

  return (
    <>
      <Topbar
        leading={
          <Button
            className="wb-icon-btn"
            title="목록으로"
            appearance='outline'
            color='gray'
            onClick={async () => {
              const backPath = fromTemplates ? '/templates' : '/workflows';
              // 새 워크플로우(미저장)이면 삭제 확인
              if (workflow?.name === '새 워크플로우') {
                if (confirm('저장하지 않으면 이 워크플로우가 삭제됩니다. 나가시겠습니까?')) {
                  try {
                    await workflowApi.delete(workflow.id);
                  } catch {
                    // 삭제 실패해도 목록으로 이동
                  }
                  navigate(backPath);
                }
              } else {
                navigate(backPath);
              }
            }}
          >←
          </Button>
        }
        title={
          // 제목 - 제목 · 연필 아이콘 클릭 시 input 으로 전환, blur · Enter 시 자동 저장
          inlineEdit === 'name' ? (
            <input
              className="wb-inline-input wb-inline-title input--md"
              value={inlineDraft}
              placeholder="워크플로우 이름"
              autoFocus
              onChange={(e) => setInlineDraft(e.target.value)}
              onBlur={commitInlineEdit}
              onKeyDown={handleInlineKeyDown}
            />
          ) : canEdit ? (
            <>
              <span className={`wb-inline-text ${workflow.name ? '' : 'empty'}`} onClick={() => startInlineEdit('name')} title={workflow.name}>
                {workflow.name || '이름 추가'}
              </span>
              <button type="button" className="wb-inline-edit-btn" aria-label="이름 수정" title="이름 수정" onClick={() => startInlineEdit('name')}>
                <EditIcon />
              </button>
            </>
          ) : workflow.name
        }
        badge={
          workflow.id === 'new' ? null : (
            <>
              <span className="wb-title-meta">
                마지막 저장 : {formatDateTime(workflow.updatedAt)}
                {workflow.isSharedAsChatbot && ' · 챗봇 공유됨'}
                {workflow.isSharedAsTemplate && ' · 템플릿 공유됨'}
              </span>
            </>
          )
        }
        subtitle={
          // 설명 - 클릭 시 input 으로 전환, blur · Enter 시 자동 저장
          inlineEdit === 'description' ? (
            <input
              className="wb-inline-input wb-inline-desc"
              value={inlineDraft}
              placeholder="설명을 입력하세요"
              autoFocus
              onChange={(e) => setInlineDraft(e.target.value)}
              onBlur={commitInlineEdit}
              onKeyDown={handleInlineKeyDown}
            />
          ) : canEdit ? (
            <>
              <span
                className={`wb-inline-text wb-inline-desc-text ${workflow.description ? '' : 'empty'}`}
                onClick={() => startInlineEdit('description')}
                title={workflow.description}
              >
                {workflow.description || '설명 추가'}
              </span>
              <button type="button" className="wb-inline-edit-btn" onClick={() => startInlineEdit('description')} aria-label="설명 수정" title="설명 수정">
                <EditIcon />
              </button>
            </>
          ) : (workflow.description || undefined)
        }
        actions={
        <div className="wb-header-actions">
          {/* 워크플로우 정보 팝오버 */}
          <Popover
            open={showInfoPopover}
            onClose={closeInfoPopover}
            title="워크플로우 정보"
            trigger={
              <Button
                type="button"
                className={`wb-icon-btn ${showInfoPopover ? 'active' : ''}`}
                aria-label="워크플로우 정보"
                title="워크플로우 정보"
                onClick={() => setShowInfoPopover((prev) => !prev)}
                appearance='outline'
                color='gray'
              >
                <InfoIcon />
              </Button>
            }
            footer={canEdit && (
              <Button
                color="primary"
                onClick={() => { setShowInfoPopover(false); openInfoModal(); }}
              >
                정보 수정
              </Button>
            )}
          >
                <div className="wb-info-label">워크플로우 ID</div>
                <div className="wb-info-id">
                  <code>{workflow.id}</code>
                  <button type="button" className="wb-copy-btn" onClick={copyWorkflowId}>
                    <CopyIcon />
                  </button>
                </div>
                {/* 아이콘 + 항목 이름 | 값 (테두리 상자 안 줄 구분) */}
                <dl className="wb-info-list">
                  <div className="wb-info-row">
                    <dt><UserIcon />만든 사람</dt>
                    <dd>{workflow.ownerName || '-'}</dd>
                  </div>
                  <div className="wb-info-row">
                    <dt><CalendarIcon />만든 날짜</dt>
                    <dd>{formatDateTime(workflow.createdAt)}</dd>
                  </div>
                  <div className="wb-info-row">
                    <dt><DescriptionIcon />설명</dt>
                    <dd>{workflow.description || '-'}</dd>
                  </div>
                </dl>
          </Popover>

          {!canEdit && (
            <Button color="gray" appearance="outline" onClick={handleCopy} className='wb-text-btn'>
              복사하기
            </Button>
          )}

          {/* 스케줄 정지/재가동 버튼 — 스케줄 트리거인 경우만 */}
          {canEdit && workflow.nodes.some(n => n.type === 'schedule-trigger') && (
            isScheduleActive ? (
              <Button
                color="gray"
                appearance="outline"
                onClick={handleSchedulePause}
                disabled={scheduleActing}
                title="스케줄 자동 실행 정지"
              >
                <PauseIcon className="wb-fill-icon" />
                {scheduleActing ? '처리 중...' : '스케줄 정지'}
              </Button>
            ) : (
              <Button
                color="gray"
                appearance="outline"
                onClick={handleScheduleResume}
                disabled={scheduleActing}
                title="스케줄 자동 실행 재가동"
              >
                <PlayIcon className="wb-fill-icon" />
                {scheduleActing ? '처리 중...' : '스케줄 재가동'}
              </Button>
            )
          )}

          {/* 공유하기 / 공유취소 - 누르면 모달에서 챗봇 · 템플릿 선택 후 공유 / 공유취소 (챗봇은 자동 실행 트리거 없는 워크플로우만)
              모두 공유된 상태면 버튼 이름이 공유취소 */}
          {canEdit && isAdmin && (
            <Button
              color="gray"
              appearance="outline"
              onClick={openShareModal}
              disabled={saving || workflow.id === 'new'}
              title={
                workflow.id === 'new' ? '먼저 저장해야 공유할 수 있습니다' :
                allShared ? '모두 공유됨 - 공유취소할 항목 선택' : ''
              }
            >
              <ShareIcon />
              공유 설정
            </Button>
          )}


          {canEdit && (
            <Button color="primary" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          )}
        </div>
        }
      />

      <div className="page-content">
        <div className="builder-layout">
          {/* Palette */}
          <div className="builder-item palette">
            <div className="builder-item-header">설치된 도구</div>
            <div className='builder-item-body' key={paletteVersion}>
              {Object.entries(getNodeTypesByCategory()).map(([category, nodes]) => {
                if (nodes.length === 0) return null;
                const isCollapsed = collapsedCategories[category];
                const categoryInfo = CATEGORY_INFO[category] || { label: category };
                
                return (
                  <div key={category} className="palette-category">
                    <div 
                      className="palette-category-header"
                      onClick={() => setCollapsedCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                    >
                      <span className="palette-category-label">{categoryInfo.label}</span>
                      <span className="palette-category-count">{nodes.length}</span>
                    </div>
                    {!isCollapsed && (
                      <div className="palette-category-items">
                        {nodes.map((nodeType) => (
                          <div
                            key={nodeType.type}
                            className="palette-item"
                            draggable={canEdit}
                            onDragStart={(e) => canEdit && handlePaletteDragStart(e, nodeType)}
                            onDragEnd={handlePaletteDragEnd}
                            style={{ cursor: canEdit ? 'grab' : 'default', opacity: canEdit ? 1 : 0.5 }}
                          >
                            {renderNodeIcon(nodeType.type)}
                            <span>{nodeType.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Canvas */}
          <div
            ref={canvasRef}
            className={`canvas ${draggedPaletteItem ? 'drop-target' : ''}`}
            onDragOver={handleCanvasDragOver}
            onDrop={handleCanvasDrop}
          >
            {/* SVG for edges */}
            <svg ref={svgRef} className="edge-svg">
              {/* 실제 엣지 */}
              {workflow.edges.map((edge) => {
                const fromNode = workflow.nodes.find((n) => n.id === edge.source);
                const toNode = workflow.nodes.find((n) => n.id === edge.target);
                if (!fromNode || !toNode) return null;

                const p1 = getPortPosition(fromNode, (edge.sourceHandle || 'out') as PortType);
                const p2 = getPortPosition(toNode, (edge.targetHandle || 'in') as PortType);
                const path = getBezierPath(p1, p2, (edge.sourceHandle || 'out') as PortType, (edge.targetHandle || 'in') as PortType);

                return (
                  <path
                    key={edge.id}
                    d={path}
                    stroke="var(--primary)"
                    strokeWidth="2"
                    fill="none"
                    className="edge-path"
                  />
                );
              })}

              {/* 연결 중인 선 */}
              {connectingLine && (() => {
                const fromNode = workflow.nodes.find((n) => n.id === connectingLine.fromNodeId);
                if (!fromNode) return null;

                const p1 = getPortPosition(fromNode, connectingLine.fromPort);
                const path = getBezierPath(p1, { x: connectingLine.toX, y: connectingLine.toY }, connectingLine.fromPort, 'in');

                return (
                  <path
                    d={path}
                    stroke="var(--primary)"
                    strokeWidth="2"
                    strokeDasharray="4 3"
                    fill="none"
                  />
                );
              })()}
            </svg>

            {/* 힌트 */}
            {workflow.nodes.length === 0 && (
              <div className="hint">
                <img className="hint-illust" src={isAdmin ? adminEmptyCanvas : userEmptyCanvas} alt="" />
                <span>왼쪽 팔레트에서 노드를 드래그하여 워크플로우를 구성하세요</span>
              </div>
            )}

            {/* 노드들 */}
            {workflow.nodes.map((node) => {
              const nodeTypes = getNodeTypes();
              const nodeInfo = nodeTypes.find((t) => t.type === node.type)
                || { label: MARKETPLACE_ITEMS.find(m => m.nodeType === node.type)?.title || node.type };
              // 설정 가능한 노드 타입들
              const configurableNodes = [
                'knowledge-retrieval', 'llm', 'condition', 'output', 'notify',
                'mail-trigger', 'file-trigger', 'webhook-trigger', 'schedule-trigger',
                'web-search', 'http-call', 'email-send', 'summarize', 'file-gen',
                'google-drive', 'slack', 'notion', 'jira', 'gmail', 'email', 'naver', 'mcp', 'dropbox'
              ];
              const hasConfig = configurableNodes.includes(node.type);

              return (
                <div
                  key={node.id}
                  data-id={node.id}
                  className={`wnode ${hasConfig ? 'has-config' : ''}`}
                  style={{
                    left: node.position.x,
                    top: node.position.y,
                    cursor: draggingNodeId === node.id ? 'grabbing' : 'grab',
                  }}
                  onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                >
                  {canEdit && <button className="wnode-del" onClick={() => deleteNode(node.id)}>✕</button>}
                  
                  {/* 설정 버튼 */}
                  {hasConfig && canEdit && (
                    <button
                      className="wnode-config-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openConfigModal(node);
                      }}
                      title="상세 설정"
                    >
                      <SettingIcon/>
                    </button>
                  )}
                  
                  {/* 4방향 포트 */}
                  <div
                    className="port in"
                    data-port="in"
                    onMouseDown={(e) => handlePortMouseDown(e, node.id, 'in')}
                  />
                  <div
                    className="port out"
                    data-port="out"
                    onMouseDown={(e) => handlePortMouseDown(e, node.id, 'out')}
                  />
                  <div
                    className="port top"
                    data-port="top"
                    onMouseDown={(e) => handlePortMouseDown(e, node.id, 'top')}
                  />
                  <div
                    className="port bottom"
                    data-port="bottom"
                    onMouseDown={(e) => handlePortMouseDown(e, node.id, 'bottom')}
                  />

                  <div className="wnode-inner">
                    {renderNodeIcon(node.type)}
                    <div className="wnode-text">
                      <span className="wnode-label" title={nodeInfo.label}>{nodeInfo.label}</span>
                      {'category' in nodeInfo && <span className="wnode-category">{nodeInfo.category}</span>}
                    </div>
                  </div>

                  {node.type === 'knowledge-retrieval' && (
                    <div className="wnode-config-row">
                      {canEdit ? (
                        <Select
                          size="sm"
                          className={`wnode-kb-select ${!node.data?.knowledgeBaseId ? 'unset' : ''}`}
                          value={(node.data?.knowledgeBaseId as string) || ''}
                          onChange={(e) => updateNodeData(node.id, { knowledgeBaseId: e.target.value })}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">지식베이스 선택...</option>
                          {knowledgeBases.map((kb) => (
                            <option key={kb.id} value={kb.id}>{kb.name}</option>
                          ))}
                        </Select>
                      ) : (
                        <span className="wnode-kb-readonly" onClick={(e) => e.stopPropagation()}>
                          {knowledgeBases.find(kb => kb.id === node.data?.knowledgeBaseId)?.name?.replace(' (읽기 전용)', '') || '지식베이스 미설정'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* 엣지 삭제 버튼 */}
            {canEdit && workflow.edges.map((edge) => {
              const midpoint = getEdgeMidpoint(edge);
              return (
                <button
                  key={`del-${edge.id}`}
                  className="edge-del"
                  style={{ left: midpoint.x, top: midpoint.y }}
                  onClick={() => deleteEdge(edge.id)}
                >
                  ✕
                </button>
              );
            })}
          </div>

          {/* 오른쪽 패널 접힘 - 캔버스 오른쪽 위 알약 버튼 (누르면 그 탭으로 펼침) */}
          {sidePanelCollapsed && !fromTemplates && (
            <div className="side-panel-pill" role="group" aria-label="오른쪽 패널 열기">
              <button type="button" className="side-panel-pill-btn" onClick={() => openSidePanel('examples')}>
                <GridIcon />
                예시 워크플로우
                <span className="tab-count">3</span>
              </button>
              <span className="side-panel-pill-divider" />
              {hasRunTrigger && (
              <button type="button" className="side-panel-pill-btn" onClick={() => openSidePanel('logs')}>
                <ListIcon />
                실행 로그
                {failedLogCount > 0 && <Badge color="error" dot>실패 {failedLogCount}</Badge>}
              </button>
              )}
            </div>
          )}

          {/* 오른쪽 패널 - 예시 워크플로우 / 실행 로그 탭 */}
          {/* 패널 접기 핸들 - 오른쪽 패널 왼쪽 경계선 위에 걸침 */}
          {!sidePanelCollapsed && !fromTemplates && (
            <button
              type="button"
              className="side-panel-collapse-btn"
              onClick={() => setSidePanelCollapsed(true)}
              title="패널 접기"
              aria-label="패널 접기"
            >
              <PanelCollapseIcon />
            </button>
          )}

          {!sidePanelCollapsed && !fromTemplates && (
          <aside className="example-section">
            <div className="builder-tabs-bar">
              <Tabs
                className="builder-tabs"
                items={[
                  { key: 'examples', label:(<>예시 워크플로우<span className='tab-count'>3</span></>) },
                  ...(hasRunTrigger ? [{
                    key: 'logs',
                    label: (
                      <>
                        실행 로그
                        {failedLogCount > 0 && <Badge color="error" dot>실패 {failedLogCount}</Badge>}
                      </>
                    ),
                  }] : []),
                ]}
                active={showLogPanel ? 'logs' : 'examples'}
                onChange={(key) => { if ((key === 'logs') !== showLogPanel) toggleLogPanel(); }}
              />
            </div>
            {!showLogPanel && (
            <div className='builder-item-body'>
              {/* RAG 챗봇 */}
              <div className="example-card">
                <div className='example-header'>
                  <div className="example-title">사내 문서 RAG 챗봇</div>
                  <Button color="primary" appearance="fill" className="example-load-btn" onClick={loadRagExample} size='sm'>불러오기</Button>
                </div>
                {renderExampleFlow(EXAMPLE_RAG_STEPS)}
                <div className="example-desc">
                  지식베이스 문서를 근거로 사내 질문에 답하는 챗봇
                </div>
                <div className="example-need">필요 : 지식베이스 연결</div>
              </div>

              {/* DB 조회 자동 보고 */}
              <div className="example-card">
                <div className='example-header'>
                  <div className="example-title">DB 조회 자동 보고 메일</div>
                  <Button color="primary" appearance="fill" className="example-load-btn" onClick={loadDbReportExample} size='sm'>불러오기</Button>
                </div>
                {renderExampleFlow(EXAMPLE_DB_REPORT_STEPS)}
                <div className="example-desc">
                  매일 정해진 시간에 DB를 조회해 결과를 HTML 표 형식 메일로 보내기
                </div>
                <div className="example-need">필요 : Text-to-SQL 지식베이스, 이메일 커넥터 연결</div>
              </div>

              {/* Slack 알림 */}
              <div className="example-card">
                <div className='example-header'>
                  <div className="example-title">웹 검색 → Slack 알림</div>
                  <Button color="primary" appearance="fill" className="example-load-btn" onClick={loadSlackAlertExample} size='sm'>불러오기</Button>
                </div>
                {renderExampleFlow(EXAMPLE_SLACK_ALERT_STEPS)}
                <div className="example-desc">
                  사용자 질문에 맞춰 웹을 검색하고 결과를 Slack 채널에 알림
                </div>
                <div className="example-need">필요 : 웹 검색, Slack 계정 연결</div>
              </div>
            </div>
            )}

            {/* 실행 로그 탭 */}
            {showLogPanel && (
              <div className="builder-item-body run-log-body">
                <div className="run-log-toolbar">
                  <button className="run-log-refresh-btn" onClick={loadLogs} disabled={logsLoading} title="새로고침" aria-label="새로고침">
                    <RefreshIcon className={logsLoading ? 'spinning' : ''} />
                  </button>
                  
                  {canEdit && workflow?.status === 'deployed' && (
                    <Button
                      color="success"
                      size="sm"
                      className="run-log-run-btn"
                      onClick={handleManualRun}
                      disabled={running}
                      title="지금 바로 실행"
                    >
                      <PlayIcon />
                      {running ? '실행 중...' : '지금 실행'}
                    </Button>
                  )}
                </div>

                {logsLoading ? (
                  <div className="run-log-empty">로그를 불러오는 중...</div>
                ) : runLogs.length === 0 ? (
                  <div className="run-log-empty">아직 실행 기록이 없습니다.</div>
                ) : (
                  <ul className="run-log-list">
                    {runLogs.map((log) => (
                      <li key={log.logId} className={`run-log-item ${log.status}`}>
                        <div className="run-log-item-main">
                        <div className="run-log-item-top">
                          <span className={`wf-run-status ${log.status}`}>
                            <span className="wf-run-status-icon">
                              {log.status === 'success' ? <CheckIcon /> : log.status === 'failed' ? <CloseIcon /> : <SkipIcon />}
                            </span>
                            {log.status === 'success' ? '성공' : log.status === 'failed' ? '실패' : '스킵'}
                          </span>
                          <span className="run-log-time">{new Date(log.startedAt).toLocaleString('ko-KR')}</span>
                        </div>

                        <div className="run-log-item-meta">
                          <span className="run-log-trigger">
                            {log.triggeredBy === 'schedule'
                              ? <span title={log.metadata?.scheduleLabel ? `설정: ${log.metadata.scheduleLabel}` : undefined}>
                                  스케줄
                                  {log.metadata?.scheduleLabel && (
                                    <span className="run-log-schedule-label"> · {log.metadata.scheduleLabel}</span>
                                  )}
                                </span>
                              : '수동 실행'}
                          </span>
                          <span className="run-log-duration">
                            {log.durationMs < 1000
                              ? `${log.durationMs}ms`
                              : `${(log.durationMs / 1000).toFixed(1)}s`}
                          </span>
                        </div>
                        </div>

                        {/* 하단 상세 - 오류 메시지 또는 실행된 노드 흐름 + 쿼리 보기 */}
                        <div className="run-log-detail">
                          <span className="run-log-detail-title">상세</span>
                          {(() => {
                            if (log.errorMessage) return <span className="run-log-error">{log.errorMessage}</span>;
                            const nodes = log.nodeResults.filter(n => n.nodeType !== 'schedule-trigger' && n.nodeType !== 'trigger');
                            if (nodes.length === 0) return <span className="run-log-nodes">-</span>;
                            return (
                              <span className="run-log-nodes">
                                {nodes.map((n, i) => (
                                  <span key={i} className="run-log-node-wrap">
                                    {i > 0 && <span className="run-log-node-arrow">→</span>}
                                    {/* 실패 = 빨간 글자, 스킵 = 흐린 글자 (status 클래스) */}
                                    <span className={`run-log-node ${n.status}`}>
                                      {n.nodeType === 'knowledge-retrieval'
                                        ? <span className="wb-icon wb-icon--blue"><FlowDatabaseIcon /></span>
                                        : renderNodeIcon(n.nodeType)}
                                      {RUN_LOG_NODE_LABELS[n.nodeType] || n.nodeType}
                                    </span>
                                  </span>
                                ))}
                              </span>
                            );
                          })()}
                          {(log.metadata?.retrievalQuery || log.metadata?.executedSql) && (
                            <button
                              className="run-log-query-btn"
                              onClick={() => { setSelectedLog(selectedLog?.logId === log.logId ? null : log); setSelectedLogTab('query'); }}
                              title="실행 쿼리 확인"
                            >
                              보기
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </aside>
          )}
        </div>

        {/* 실행 쿼리 확인 모달 */}
        {selectedLog && (selectedLog.metadata?.retrievalQuery || selectedLog.metadata?.executedSql) && (
          <div className="app-config-modal-overlay" onClick={() => setSelectedLog(null)}>
            <div className="app-config-modal wide" onClick={(e) => e.stopPropagation()}>
              <div className="app-config-header">
                <span className="app-config-icon">🔎</span>
                <h3>실행 상세</h3>
                <button className="app-config-close" onClick={() => setSelectedLog(null)}>✕</button>
              </div>

              {/* 탭 */}
              <div className="run-log-modal-tabs">
                <button
                  className={`run-log-modal-tab ${selectedLogTab === 'query' ? 'active' : ''}`}
                  onClick={() => setSelectedLogTab('query')}
                >
                  🔍 쿼리 정보
                </button>
                <button
                  className={`run-log-modal-tab ${selectedLogTab === 'result' ? 'active' : ''}`}
                  onClick={() => setSelectedLogTab('result')}
                  disabled={!selectedLog.metadata?.retrievalSummary && !selectedLog.metadata?.retrievalRows?.length}
                >
                  📊 출력 결과
                </button>
              </div>

              <div className="app-config-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {selectedLogTab === 'query' && (
                  <div className="run-log-detail-panel" style={{ background: 'transparent', padding: 0 }}>
                    <div className="run-log-detail-item">
                      <span className="run-log-detail-label">실행 시간</span>
                      <span className="run-log-detail-value">
                        {new Date(selectedLog.startedAt).toLocaleString('ko-KR')}
                        {' '}({selectedLog.durationMs < 1000 ? `${selectedLog.durationMs}ms` : `${(selectedLog.durationMs / 1000).toFixed(1)}s`})
                      </span>
                    </div>
                    {selectedLog.metadata?.retrievalQuery && (
                      <div className="run-log-detail-item">
                        <span className="run-log-detail-label">자연어 쿼리</span>
                        <span className="run-log-detail-value">{selectedLog.metadata.retrievalQuery}</span>
                      </div>
                    )}
                    {selectedLog.metadata?.executedSql && (
                      <div className="run-log-detail-item">
                        <span className="run-log-detail-label">실행 SQL</span>
                        <code className="run-log-detail-sql">{selectedLog.metadata.executedSql}</code>
                      </div>
                    )}
                    {selectedLog.metadata?.rowCount !== undefined && (
                      <div className="run-log-detail-item">
                        <span className="run-log-detail-label">결과 행 수</span>
                        <span className="run-log-detail-value">{selectedLog.metadata.rowCount}행</span>
                      </div>
                    )}
                  </div>
                )}

                {selectedLogTab === 'result' && (
                  <div className="run-log-result-panel">
                    {selectedLog.metadata?.retrievalSummary && (
                      <div className="run-log-detail-item" style={{ marginBottom: '16px' }}>
                        <span className="run-log-detail-label">AI 요약</span>
                        <div
                          className="run-log-summary"
                          dangerouslySetInnerHTML={{ __html: markdownToHtml(selectedLog.metadata.retrievalSummary) }}
                        />
                      </div>
                    )}
                    {selectedLog.metadata?.retrievalRows && selectedLog.metadata.retrievalRows.length > 0 && (
                      <div className="run-log-detail-item">
                        <span className="run-log-detail-label">
                          데이터 ({selectedLog.metadata.retrievalRows.length}행)
                        </span>
                        <div style={{ overflowX: 'auto', marginTop: '6px' }}>
                          <table className="run-log-result-table">
                            <thead>
                              <tr>
                                {Object.keys(selectedLog.metadata.retrievalRows[0]).map(col => (
                                  <th key={col}>{col}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {selectedLog.metadata.retrievalRows.map((row, i) => (
                                <tr key={i}>
                                  {Object.values(row).map((val, j) => {
                                    const num = typeof val === 'number' ? val : (typeof val === 'string' && /^\d+(\.\d+)?$/.test(val.trim()) ? Number(val) : null);
                                    const display = num !== null && !isNaN(num)
                                      ? num.toLocaleString('ko-KR')
                                      : String(val ?? '');
                                    return <td key={j}>{display}</td>;
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {!selectedLog.metadata?.retrievalSummary && !selectedLog.metadata?.retrievalRows?.length && (
                      <div style={{ color: 'var(--text-3)', fontSize: '13px', padding: '20px 0', textAlign: 'center' }}>
                        출력 결과가 없습니다.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="app-config-footer">
                <button className="btn-secondary" onClick={() => setSelectedLog(null)}>닫기</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 노드 설정 모달 */}
      {/* 공통 Modal - 크기: 기본 sm(420) / 넓은 설정(기존 wide · notion-wide) md(700)
          드래그 이동은 기존 핸들러 그대로 연결, 내용은 type 별 app-config-body */}
      {configModalNode && (
        <Modal
          isOpen
          onClose={closeConfigModal}
          size={(() => {
            const type = configModalNode.type;
            if (['llm', 'condition', 'output', 'notify', 'http-call', 'email-send', 'gmail', 'email', 'notion', 'google-drive', 'dropbox', 'naver', 'web-search', 'slack','mcp'].includes(type)) return 'md';
            if (type === 'knowledge-retrieval') {
              const kbId = (tempNodeConfig as any)?.knowledgeBaseId;
              const kb = knowledgeBases.find(k => k.id === kbId);
              const sources: any[] = (kb?.pipelineConfig as any)?.sources ?? [];
              const dbSrc = sources.find((s: any) => s.type === 'database') ?? (kb?.pipelineConfig as any)?.dbConfig;
              return dbSrc?.mode === 'text2sql' ? 'md' : 'sm';
            }
            return 'sm';
          })()}
          className="node-config-modal"
          style={{ transform: `translate(${configModalPos.x}px, ${configModalPos.y}px)` }}
          headerProps={{ style: { cursor: 'move' }, onMouseDown: (e) => handleModalDragStart(e, 'config') }}
          title={(() => {
            const titles: Record<string, string> = {
              llm: 'AI 응답 설정', condition: '판단 조건 설정', 'knowledge-retrieval': '문서 검색 설정',
              output: '답변 전송 설정', notify: '담당자 전송 설정',
              'mail-trigger': '메일 트리거 설정', 'file-trigger': '파일 트리거 설정',
              'webhook-trigger': '웹훅 트리거 설정', 'schedule-trigger': '스케줄 트리거 설정',
              'web-search': '웹 검색 설정', 'http-call': 'HTTP 요청 설정',
              'email-send': '이메일 발송 설정', summarize: '요약 설정', 'file-gen': '문서 생성 설정',
              'google-drive': 'Google Drive 설정', slack: 'Slack 설정', notion: 'Notion 설정', jira: 'Jira 설정',
              gmail: 'Gmail 설정', email: '이메일 커넥터 설정', naver: '네이버 메일 설정', mcp: 'MCP 서버 설정',
              dropbox: 'Dropbox 설정',
            };
            return titles[configModalNode.type] || '노드 설정';
          })()}
          subHeader={
            configModalNode.type === 'google-drive' ? <GoogleDriveSteps config={tempNodeConfig as GoogleDriveConfig} />
            : configModalNode.type === 'dropbox' ? <DropboxSteps config={tempNodeConfig as DropboxConfig} />
            : configModalNode.type === 'slack' ? <SlackSteps config={tempNodeConfig as SlackConfig} />
            : configModalNode.type === 'notion' ? <NotionSteps config={tempNodeConfig as NotionConfig} />
            : configModalNode.type === 'mcp' ? <McpSteps config={tempNodeConfig as McpConfig} />
            : configModalNode.type === 'gmail' ? <GmailSteps config={tempNodeConfig as GmailConfig} />
            : undefined
          }
          footerRight={(() => {
            // 단계형 노드 (Drive · Gmail · Slack · Notion · MCP)
            //  - 단계 이동 버튼은 각 모달에서 #node-config-footer-actions 로 포털
            //  - 중간 저장(저장 후 닫기): 연결 확인 · 연결 테스트 단계만 / 마지막 단계: 공통 저장
            const setup = getNodeSetupFooter(configModalNode.type, tempNodeConfig);
            if (setup) {
              return (
                <>
                  {/* 중간 저장 단계: 취소 대신 저장 후 닫기 (저장 없이 닫기는 헤더 ✕) */}
                  {setup.midSave ? (
                    <Button color="gray" appearance="outline" onClick={saveNodeConfig}>저장 후 닫기</Button>
                  ) : (
                    <Button color="gray" appearance="outline" onClick={closeConfigModal}>취소</Button>
                  )}
                  <span id="node-config-footer-actions" className="node-config-footer-slot" />
                  {setup.step === setup.last && (
                    <Button color="primary" onClick={saveNodeConfig}>저장</Button>
                  )}
                </>
              );
            }
            return (
              <>
                <Button color="gray" appearance="outline" onClick={closeConfigModal}>취소</Button>
                <Button color="primary" onClick={saveNodeConfig}>저장</Button>
              </>
            );
          })()}
        >

            {/* 모달 본문 - AI 응답 노드 */}
            {configModalNode.type === 'llm' && (() => {
              const currentUser = authApi.getUser();
              const role = currentUser?.role || '';
              const needsTeamSelect = ['tenantAdmin', 'customerAdmin', 'productAdmin', 'admin'].includes(role);
              const llmCfg = tempNodeConfig as LLMNodeConfig;
              return (
              <>
                {/* 팀 선택 (tenantAdmin/customerAdmin/productAdmin 전용) */}
                {needsTeamSelect && (
                  <div className="form-grid" style={{ marginBottom: 4 }}>
                    {/* 제품/테넌트 관리자: 고객사 드롭다운 먼저 */}
                    {['productAdmin', 'tenantAdmin', 'admin'].includes(role) && (
                      <div className="form-item">
                        <label className="form-label">고객사 <span className="required">*</span></label>
                        <Select
                          value={llmCustomerId}
                          onChange={(e) => {
                            setLlmCustomerId(e.target.value);
                            setTempNodeConfig({ ...tempNodeConfig, teamId: undefined, models: [], customerId: e.target.value || undefined });
                            setBedrockModels([]);
                            setConnectedProviders(prev => prev.filter(p => p.id !== 'bedrock'));
                          }}
                        >
                          <option value="">고객사 선택</option>
                          {llmTeamGroups.map(g => (
                            <option key={g.customerId} value={g.customerId}>{g.customerName}</option>
                          ))}
                        </Select>
                      </div>
                    )}
                    <div className="form-item">
                      <label className="form-label">
                        팀 (Bedrock 계정) <span className="required">*</span>
                        {llmTeamModelsLoading && <span style={{ color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>로딩 중...</span>}
                      </label>
                      <Select
                        value={llmCfg.teamId || ''}
                        disabled={['productAdmin', 'tenantAdmin', 'admin'].includes(role) && !llmCustomerId && !llmCfg.teamId}
                        onChange={async (e) => {
                          const teamId = e.target.value;
                          if (!teamId) {
                            setTempNodeConfig({ ...tempNodeConfig, teamId: undefined, models: [] });
                            setBedrockModels([]);
                            setConnectedProviders(prev => prev.filter(p => p.id !== 'bedrock'));
                            return;
                          }
                          setTempNodeConfig({ ...tempNodeConfig, teamId });
                          setLlmTeamModelsLoading(true);
                          try {
                            const models = await adminApi.listBedrockModels(`team:${teamId}`);
                            setBedrockModels(models);
                            if (models.length > 0) {
                              setConnectedProviders(prev =>
                                prev.some(p => p.id === 'bedrock') ? prev : [...prev, { id: 'bedrock', name: 'Amazon Bedrock' }]
                              );
                              const novaLite = models.find(m => m.modelId.toLowerCase().includes('nova-lite'));
                              const defaultModel = novaLite || models[0];
                              setTempNodeConfig(prev => ({
                                ...prev, teamId, provider: 'bedrock',
                                customerId: llmCustomerId || (prev as LLMNodeConfig).customerId,
                                models: (prev as LLMNodeConfig).models?.length
                                  ? (prev as LLMNodeConfig).models
                                  : [defaultModel?.modelId || ''],
                              }));
                            } else {
                              setConnectedProviders(prev => prev.filter(p => p.id !== 'bedrock'));
                              setTempNodeConfig(prev => ({ ...prev, teamId, models: [] }));
                            }
                          } catch { setBedrockModels([]); }
                          finally { setLlmTeamModelsLoading(false); }
                        }}
                      >
                        <option value="">
                          {['productAdmin', 'tenantAdmin', 'admin'].includes(role) && !llmCustomerId && !llmCfg.teamId
                            ? '고객사를 먼저 선택하세요'
                            : '팀 선택 (Bedrock 계정 연동)'}
                        </option>
                        {/* 고객사 선택됨 → 해당 고객사 팀, 아니면 현재 저장된 팀만 표시 */}
                        {llmCustomerId
                          ? (llmTeamGroups.find(g => g.customerId === llmCustomerId)?.teams ?? []).map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))
                          : role === 'customerAdmin'
                            ? (llmTeamGroups[0]?.teams ?? []).map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))
                            : llmCfg.teamId
                              ? [llmTeamGroups.flatMap(g => g.teams).find(t => t.id === llmCfg.teamId)].filter(Boolean).map(t => (
                                  <option key={t!.id} value={t!.id}>{t!.name}</option>
                                ))
                              : []
                        }
                      </Select>
                    </div>
                  </div>
                )}
                <div className="form-grid form-grid--2">
                  <div className="form-item">
                    <label className='form-label'>AI 제공자</label>
                    <Select
                      value={llmCfg.provider || 'bedrock'}
                      onChange={(e) => {
                        const prov = e.target.value as 'bedrock' | 'google';
                        // 제공자 변경 시 모델 초기화
                        const models = prov === 'google' ? googleModels : bedrockModels;
                        const defaultModel = models.length > 0 ? models[0].modelId : '';
                        setTempNodeConfig({ ...tempNodeConfig, provider: prov, models: defaultModel ? [defaultModel] : [] });
                      }}
                    >
                      {connectedProviders.length === 0 && (
                        <option value="">::: 제공자 선택 :::</option>
                      )}
                      {connectedProviders.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Select>
                  </div>

                  <div className="form-item">
                    <label className='form-label'>모델 선택 <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>(복수)</span></label>
                    {(llmCfg.provider === 'google' ? googleModels : bedrockModels).length === 0 ? (
                      <div className="select" style={{ display: 'flex', alignItems: 'center', color: 'var(--text-3)', cursor: 'default' }}>
                        {needsTeamSelect ? '팀을 먼저 선택하세요' : '모델 없음'}
                      </div>
                    ) : (() => {
                      const availableModels = llmCfg.provider === 'google' ? googleModels : bedrockModels;
                      const selectedModels: string[] = llmCfg.models ?? [];
                      const summaryText = selectedModels.length === 0
                        ? '모델 선택...'
                        : selectedModels.length === 1
                          ? (availableModels.find(m => m.modelId === selectedModels[0])?.modelName || selectedModels[0])
                          : `${availableModels.find(m => m.modelId === selectedModels[0])?.modelName || selectedModels[0]} 외 ${selectedModels.length - 1}개`;
                      return (
                        <details style={{ position: 'relative', width: '100%' }}>
                          <summary className="select" style={{
                            display: 'flex', alignItems: 'center',
                            listStyle: 'none', userSelect: 'none',
                            padding: '0 32px 0 16px', height: 34,
                          }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {summaryText}
                            </span>
                          </summary>
                          <div style={{
                            position: 'absolute', zIndex: 100, left: 0, right: 0,
                            border: '1px solid var(--border)', borderRadius: 8,
                            background: 'var(--white, #fff)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                            maxHeight: 240, overflowY: 'auto', marginTop: 2,
                          }}>
                            {availableModels.map((mdl, idx, arr) => {
                              const checked = selectedModels.includes(mdl.modelId);
                              const isDefault = selectedModels[0] === mdl.modelId;
                              return (
                                <label key={mdl.modelId} style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  padding: '7px 14px', cursor: 'pointer',
                                  background: checked ? 'rgba(99,102,241,0.08)' : 'var(--white, #fff)',
                                  borderBottom: idx < arr.length - 1 ? '1px solid var(--border)' : 'none',
                                  fontSize: 13,
                                }}>
                                  <input type="checkbox" checked={checked} onChange={(e) => {
                                    const current: string[] = llmCfg.models ?? [];
                                    const next = e.target.checked
                                      ? [...current, mdl.modelId]
                                      : current.filter(m => m !== mdl.modelId);
                                    setTempNodeConfig({ ...tempNodeConfig, models: next });
                                  }} />
                                  <span style={{ flex: 1 }}>{mdl.modelName}</span>
                                  {isDefault && <span style={{ fontSize: 10, color: '#6366f1', fontWeight: 600 }}>기본</span>}
                                </label>
                              );
                            })}
                          </div>
                        </details>
                      );
                    })()}
                  </div>

                  <div className="form-item">
                    <label className='form-label'>Temperature (창의성)</label>
                    <Select
                      value={(tempNodeConfig as LLMNodeConfig).temperature || '0.5'}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, temperature: e.target.value })}
                    >
                      <option value="0">0 - 정확하고 일관된 응답</option>
                      <option value="0.3">0.3 - 약간의 다양성</option>
                      <option value="0.5">0.5 - 균형 잡힌 응답</option>
                      <option value="0.7">0.7 - 창의적 응답</option>
                      <option value="1.0">1.0 - 매우 창의적</option>
                    </Select>
                  </div>
                </div>

                <div className='form-grid'>
                  <div className="form-item">
                    <label className='form-label'>시스템 프롬프트</label>
                    <textarea
                      className="config-textarea"
                      rows={12}
                      placeholder={DEFAULT_LLM_SYSTEM_PROMPT}
                      value={(tempNodeConfig as LLMNodeConfig).systemPrompt || ''}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, systemPrompt: e.target.value })}
                    />
                  </div>
                </div>
                <div className='form-grid form-grid--2'>
                  <div className="form-item">
                    <label className='form-label'>응답 언어</label>
                    <Select
                      value={(tempNodeConfig as LLMNodeConfig).responseLanguage || 'auto'}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, responseLanguage: e.target.value })}
                    >
                      <option value="auto">자동 감지 (질문 언어 따름)</option>
                      <option value="ko">한국어</option>
                      <option value="en">영어</option>
                      <option value="ja">일본어</option>
                      <option value="zh">중국어</option>
                    </Select>
                  </div>

                  <div className="form-item">
                    <label className="form-label">응답 형식</label>
                    <Select
                      value={(tempNodeConfig as LLMNodeConfig).responseFormat || 'text'}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, responseFormat: e.target.value })}
                    >
                      <option value="text">일반 텍스트</option>
                      <option value="markdown">마크다운</option>
                      <option value="bullet">불릿 포인트</option>
                      <option value="numbered">번호 목록</option>
                    </Select>
                  </div>

                  <div className="form-item">
                    <label className="form-label">응답 길이</label>
                    <Select
                      value={(tempNodeConfig as LLMNodeConfig).responseLength || 'medium'}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, responseLength: e.target.value })}
                    >
                      <option value="short">짧게 (1-2문장)</option>
                      <option value="medium">보통 (1-2문단)</option>
                      <option value="long">길게 (상세 설명)</option>
                    </Select>
                  </div>

                  <div className="form-item">
                    <label className="form-label">최대 토큰</label>
                    <input
                      type="number"
                      placeholder="4096"
                      value={(tempNodeConfig as LLMNodeConfig).maxTokens || ''}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, maxTokens: parseInt(e.target.value) || undefined })}
                    />
                  </div>

                  <div className="form-item">
                    <label className="form-label">대화 컨텍스트</label>
                    <Select
                      value={(tempNodeConfig as LLMNodeConfig).contextWindow || '5'}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, contextWindow: e.target.value })}
                    >
                      <option value="0">현재 메시지만</option>
                      <option value="3">최근 3개 메시지</option>
                      <option value="5">최근 5개 메시지 (기본)</option>
                      <option value="10">최근 10개 메시지</option>
                      <option value="all">전체 대화</option>
                    </Select>
                  </div>

                  <div className="form-item">
                    <label className="form-label">스트리밍 응답</label>
                    <Select
                      value={(tempNodeConfig as LLMNodeConfig).streaming !== false ? 'true' : 'false'}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, streaming: e.target.value === 'true' })}
                    >
                      <option value="true">켜기 (글자 단위로 표시)</option>
                      <option value="false">끄기 (한번에 표시)</option>
                    </Select>
                  </div>

                  <div className="form-item">
                    <label className="form-label">출처 표시</label>
                    <Select
                      value={(tempNodeConfig as LLMNodeConfig).showSources !== false ? 'true' : 'false'}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, showSources: e.target.value === 'true' })}
                    >
                      <option value="true">표시 (문서명, 유사도 점수)</option>
                      <option value="false">숨기기</option>
                    </Select>
                  </div>
                </div>
              </>
              );
            })()}

            {/* 모달 본문 - 문서 검색 노드 */}
            {configModalNode.type === 'knowledge-retrieval' && (() => {
              const kbId = (tempNodeConfig as KnowledgeRetrievalConfig).knowledgeBaseId;
              const selectedKb = knowledgeBases.find(kb => kb.id === kbId);
              const cfg = selectedKb?.pipelineConfig as any;
              const sources: any[] = cfg?.sources ?? [];
              const dbSrc = sources.find((s: any) => s.type === 'database') ?? cfg?.dbConfig;
              const isText2Sql = dbSrc?.mode === 'text2sql';
              return (
              <div className=""
                style={isText2Sql
                  ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px', alignItems: 'start' }
                  : { display: 'flex', flexDirection: 'column', gap: '18px' }}
              >
                {/* 왼쪽: KB 선택 + TopK + 유사도 */}
                <div className='bedrock-guide'>
                  <div className="bedrock-guide-section">
                    <div className='bedrock-guide-title'>지식베이스 선택</div>
                    <Select
                      value={(tempNodeConfig as KnowledgeRetrievalConfig).knowledgeBaseId || ''}
                      onChange={(e) => {
                        const kbId = e.target.value;
                        const selectedKb = knowledgeBases.find(kb => kb.id === kbId);
                        const kbSearch = (selectedKb?.pipelineConfig as any)?.search;
                        const newCfg = (selectedKb?.pipelineConfig as any);
                        const newSources: any[] = newCfg?.sources ?? [];
                        const newDbSrc = newSources.find((s: any) => s.type === 'database') ?? newCfg?.dbConfig;
                        const newIsText2Sql = newDbSrc?.mode === 'text2sql';
                        // KB 기본값으로 초기화 (노드에 이미 값이 없는 경우)
                        const currentConfig = tempNodeConfig as KnowledgeRetrievalConfig;
                        setTempNodeConfig({
                          ...tempNodeConfig,
                          knowledgeBaseId: kbId,
                          // 노드에 저장된 값이 없으면 KB 기본값으로 채움
                          topK: currentConfig.topK ?? (kbSearch?.topK ?? 5),
                          similarityThreshold: currentConfig.similarityThreshold ?? (kbSearch?.similarityThreshold ?? 0.3),
                          // KB 변경 시 Text-to-SQL 토글 초기값: text2sql KB면 ON, 아니면 OFF
                          useText2Sql: newIsText2Sql,
                        });
                      }}
                    >
                      <option value="">지식베이스를 선택하세요...</option>
                      {knowledgeBases.map((kb) => (
                        <option key={kb.id} value={kb.id}>{kb.name}</option>
                      ))}
                    </Select>
                  </div>

                  <Notice color="info">
                    아래 설정은 지식베이스 기본 설정을 <strong>오버라이드</strong>합니다. 이 워크플로우에서만 다르게 적용하려는 경우에만 변경하세요.
                  </Notice>

                  <div className="bedrock-guide-section">
                    <div className='bedrock-guide-title'>검색 결과 개수 (Top K)</div>
                    {(() => {
                      const kbId = (tempNodeConfig as KnowledgeRetrievalConfig).knowledgeBaseId;
                      const selectedKb = knowledgeBases.find(kb => kb.id === kbId);
                      const kbTopK = (selectedKb?.pipelineConfig as any)?.search?.topK ?? 5;
                      const currentTopK = (tempNodeConfig as KnowledgeRetrievalConfig).topK ?? kbTopK;
                      return (
                        <Select
                          value={currentTopK}
                          onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, topK: parseInt(e.target.value) })}
                        >
                          {[1, 3, 5, 10, 20].map(v => (
                            <option key={v} value={v}>{v}개{v === kbTopK ? ' (KB 기본값)' : ''}</option>
                          ))}
                        </Select>
                      );
                    })()}
                  </div>

                  <div className="bedrock-guide-section">
                    <div className='bedrock-guide-title'>유사도 임계값</div>
                    {(() => {
                      const kbId = (tempNodeConfig as KnowledgeRetrievalConfig).knowledgeBaseId;
                      const selectedKb = knowledgeBases.find(kb => kb.id === kbId);
                      const kbThreshold = (selectedKb?.pipelineConfig as any)?.search?.similarityThreshold ?? 0.3;
                      const currentThreshold = (tempNodeConfig as KnowledgeRetrievalConfig).similarityThreshold ?? kbThreshold;
                      return (
                        <Select
                          value={currentThreshold}
                          onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, similarityThreshold: parseFloat(e.target.value) })}
                        >
                          {[
                            { v: 0.3, label: '0.3 - 느슨하게' },
                            { v: 0.4, label: '0.4' },
                            { v: 0.5, label: '0.5' },
                            { v: 0.6, label: '0.6' },
                            { v: 0.7, label: '0.7' },
                            { v: 0.8, label: '0.8' },
                            { v: 0.9, label: '0.9 - 엄격하게' },
                          ].map(({ v, label }) => (
                            <option key={v} value={v}>{label}{v === kbThreshold ? ' (KB 기본값)' : ''}</option>
                          ))}
                        </Select>
                      );
                    })()}
                  </div>
                </div>{/* 왼쪽 컬럼 끝 */}

                {/* 오른쪽: text2sql 전용 설정 패널 */}
                {(() => {
                  const kbId = (tempNodeConfig as KnowledgeRetrievalConfig).knowledgeBaseId;
                  const selectedKb = knowledgeBases.find(kb => kb.id === kbId);
                  const cfg = selectedKb?.pipelineConfig as any;
                  const sources: any[] = cfg?.sources ?? [];
                  const dbSrc = sources.find((s: any) => s.type === 'database') ?? cfg?.dbConfig;
                  const isText2Sql = dbSrc?.mode === 'text2sql';
                  const t2sCfg = tempNodeConfig as KnowledgeRetrievalConfig;
                  // 토글: text2sql KB면 기본 ON, 아닌 KB면 기본 OFF. 사용자가 직접 override 가능
                  const t2sEnabled = t2sCfg.useText2Sql ?? isText2Sql;
                  return (
                    <div className={`t2s-panel${t2sEnabled ? ' is-on' : ''}`}>
                      {/* 패널 헤더 - 아이콘 · 제목 · 설명 + 토글 */}
                      <div className="t2s-panel-header">
                        <span className="t2s-panel-icon"><FlowDatabaseIcon /></span>
                        <span className="t2s-panel-text">
                          <strong>Text-to-SQL</strong>
                          <span>{t2sEnabled ? '자연어 질문을 SQL로 변환해 DB를 조회합니다' : '토글을 켜면 Text-to-SQL 전용 설정이 활성화됩니다'}</span>
                        </span>
                        <button
                          type="button"
                          className={`t2s-toggle${t2sEnabled ? ' is-on' : ''}`}
                          onClick={() => setTempNodeConfig({ ...tempNodeConfig, useText2Sql: !t2sEnabled })}
                        >
                          <span className="t2s-toggle-thumb" />
                        </button>
                      </div>

                      {t2sEnabled && (
                        <div className="bedrock-guide t2s-panel-body">
                          {/* 쿼리 유형 */}
                          <div className="bedrock-guide-section">
                            <div className="bedrock-guide-title">쿼리 유형</div>
                            <Select
                              value={t2sCfg.fixedQueryType || 'natural'}
                              onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, fixedQueryType: e.target.value as 'natural' | 'sql', fixedQuery: '' })}
                            >
                              <option value="natural">자연어 — LLM이 SQL 변환</option>
                              <option value="sql">SQL 직접 입력 — LLM 생략, 바로 실행</option>
                            </Select>
                          </div>

                          {/* 고정 쿼리 */}
                          <div className="bedrock-guide-section">
                            <div className="bedrock-guide-title">고정 쿼리 {t2sCfg.fixedQueryType === 'sql' ? '(SQL)' : '(자연어)'}</div>
                            <textarea
                              className="config-textarea"
                              rows={3}
                              placeholder={
                                t2sCfg.fixedQueryType === 'sql'
                                  ? 'SELECT 영업담당자명, SUM(실제예상매출액) AS 매출합계\nFROM v_df_detail\nGROUP BY 영업담당자명\nORDER BY 매출합계 DESC'
                                  : '영업담당자별 올해 매출합계를 알려줘'
                              }
                              value={t2sCfg.fixedQuery || ''}
                              onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, fixedQuery: e.target.value })}
                            />
                            <small className="form-sublabel">
                              스케줄 자동화에서 사용. 비워두면 이전 노드의 출력값을 쿼리로 사용합니다.
                            </small>
                          </div>

                          {/* 출력 형식 */}
                          <div className="bedrock-guide-section">
                            <div className="bedrock-guide-title">출력 형식</div>
                            <Select
                              value={t2sCfg.outputFormat || 'auto'}
                              onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, outputFormat: e.target.value as KnowledgeRetrievalConfig['outputFormat'] })}
                            >
                              <option value="auto">자동 — 질문에 따라 표+차트+요약 자동 선택</option>
                              <option value="text_summary">텍스트 요약 — LLM이 자연어로 요약</option>
                              <option value="html_table">HTML 표 — 이메일 발송에 적합</option>
                              <option value="markdown_table">마크다운 표 — 챗봇 답변에 적합</option>
                              <option value="json">{'{ }'}JSON — 다음 노드에서 처리할 때</option>
                              <option value="csv">CSV — 스프레드시트 연동 시</option>
                            </Select>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {/* 오른쪽 컬럼 끝 */}
            </div>
            );})()} 

            {/* 모달 본문 - 판단 노드 */}
            {configModalNode.type === 'condition' && (
              <>
              <div className="form-grid">
                <div className="form-item">
                  <label className='form-label'>조건 유형</label>
                  <Select
                    value={(tempNodeConfig as ConditionNodeConfig).conditionType || 'similarity_score'}
                    onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, conditionType: e.target.value as ConditionNodeConfig['conditionType'] })}
                  >
                    <option value="similarity_score">유사도 점수 기반</option>
                    <option value="keyword_match">키워드 매칭</option>
                    <option value="intent_classification">의도 분류</option>
                  </Select>
                </div>
              </div>
                {/* 유사도 점수 기반 설정 */}
                {((tempNodeConfig as ConditionNodeConfig).conditionType || 'similarity_score') === 'similarity_score' && (
                  <div className="form-grid form-grid--2">
                    <div className="form-item">
                      <label className='form-label'>비교 연산자</label>
                      <Select
                        value={(tempNodeConfig as ConditionNodeConfig).similarityOperator || 'gte'}
                        onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, similarityOperator: e.target.value as ConditionNodeConfig['similarityOperator'] })}
                      >
                        <option value="gte">이상 (≥)</option>
                        <option value="gt">초과 (&gt;)</option>
                        <option value="lte">이하 (≤)</option>
                        <option value="lt">미만 (&lt;)</option>
                      </Select>
                    </div>
                    <div className="form-item">
                      <label className='form-label'>임계값 (0~1)</label>
                      <Select
                        value={(tempNodeConfig as ConditionNodeConfig).similarityThreshold || 0.5}
                        onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, similarityThreshold: parseFloat(e.target.value) })}
                      >
                        <option value="0.3">0.3 - 매우 낮음</option>
                        <option value="0.4">0.4 - 낮음</option>
                        <option value="0.5">0.5 - 보통 (기본)</option>
                        <option value="0.6">0.6 - 약간 높음</option>
                        <option value="0.7">0.7 - 높음</option>
                        <option value="0.8">0.8 - 매우 높음</option>
                      </Select>
                    </div>
                  </div>
                )}

                {/* 키워드 매칭 설정 */}
                {(tempNodeConfig as ConditionNodeConfig).conditionType === 'keyword_match' && (
                  <div className='form-grid form-grid--2'>
                    <div className="form-item">
                      <label className='form-label'>매칭 방식</label>
                      <Select
                        value={(tempNodeConfig as ConditionNodeConfig).keywordMatchType || 'any'}
                        onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, keywordMatchType: e.target.value as ConditionNodeConfig['keywordMatchType'] })}
                      >
                        <option value="any">하나라도 포함 (OR)</option>
                        <option value="all">모두 포함 (AND)</option>
                        <option value="none">포함하지 않음 (NOT)</option>
                      </Select>
                    </div>
                    <div className="form-item">
                      <label className='form-label'>키워드 (쉼표로 구분)</label>
                      <input
                        type="text"
                        placeholder="출장, 여비, 정산"
                        value={(tempNodeConfig as ConditionNodeConfig).keywords || ''}
                        onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, keywords: e.target.value })}
                      />
                    </div>
                  </div>
                )}
                {/* 의도 분류 설정 */}
                {(tempNodeConfig as ConditionNodeConfig).conditionType === 'intent_classification' && (
                <div className='form-grid'>
                  <div className="form-item">
                    <label className='form-label'>허용 의도 (쉼표로 구분)</label>
                    <input
                      type="text"
                      placeholder="질문, 문의, 요청"
                      value={(tempNodeConfig as ConditionNodeConfig).intents || ''}
                      onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, intents: e.target.value })}
                    />
                    <small style={{ color: 'var(--text-3)', marginTop: '4px', display: 'block' }}>
                      사용자 메시지가 지정된 의도 중 하나와 일치하면 참으로 판단합니다.
                    </small>
                  </div>
                </div>
                )}
              
              <div className='form-grid'>
                <div className="form-item">
                  <label className='form-label'>조건 만족 시 안내 메시지<span className='optional'>(선택)</span></label>
                  <input
                    type="text"
                    placeholder="빈칸이면 AI 응답으로 진행"
                    value={(tempNodeConfig as ConditionNodeConfig).trueBranch || ''}
                    onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, trueBranch: e.target.value })}
                  />
                </div>

                <div className="form-item">
                  <label className='form-label'>조건 불만족 시 안내 메시지<span className='optional'>(선택)</span></label>
                  <input
                    type="text"
                    placeholder="죄송합니다. 관련 문서를 찾을 수 없습니다."
                    value={(tempNodeConfig as ConditionNodeConfig).falseBranch || ''}
                    onChange={(e) => setTempNodeConfig({ ...tempNodeConfig, falseBranch: e.target.value })}
                  />
                  <small className='form-sublabel'>
                    조건이 만족되지 않으면 이 메시지를 사용자에게 표시합니다.
                  </small>
                </div>
              </div>
              </>
            )}

            {/* 답변 전송 노드 */}
            {configModalNode.type === 'output' && (
              <OutputConfigModal
                config={tempNodeConfig as OutputNodeConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* 담당자 전송 노드 */}
            {configModalNode.type === 'notify' && (
              <NotifyConfigModal
                config={tempNodeConfig as NotifyNodeConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* 메일 트리거 노드 */}
            {configModalNode.type === 'mail-trigger' && (
              <MailTriggerConfigModal
                config={tempNodeConfig as MailTriggerConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* 스케줄 트리거 노드 */}
            {configModalNode.type === 'schedule-trigger' && (
              <ScheduleTriggerConfigModal
                config={tempNodeConfig as ScheduleTriggerConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* 웹훅 트리거 노드 */}
            {configModalNode.type === 'webhook-trigger' && (
              <WebhookTriggerConfigModal
                config={tempNodeConfig as WebhookTriggerConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* HTTP 요청 노드 */}
            {configModalNode.type === 'http-call' && (
              <HttpCallConfigModal
                config={tempNodeConfig as HttpCallConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* 이메일 발송 노드 */}
            {configModalNode.type === 'email-send' && (
              <EmailSendConfigModal
                config={tempNodeConfig as EmailSendConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* Slack 노드 */}
            {configModalNode.type === 'slack' && (
              <SlackConfigModal
                config={tempNodeConfig as SlackConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* Jira 노드 */}
            {configModalNode.type === 'jira' && (
              <JiraConfigModal
                config={tempNodeConfig as JiraConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* Gmail 노드 (OAuth 방식) */}
            {configModalNode.type === 'gmail' && (
              <GmailConfigModal
                config={tempNodeConfig as GmailConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* 이메일 커넥터 노드 (SMTP/IMAP 방식) */}
            {configModalNode.type === 'email' && (
              <EmailConnectorNodeModal
                config={tempNodeConfig as EmailConnectorNodeConfig}
                onChange={(config) => setTempNodeConfig(config)}
				showErrors={configModalShowErrors}
              />
            )}

            {/* 네이버 메일 노드 */}
            {configModalNode.type === 'naver' && (
              <NaverMailConfigModal
                config={tempNodeConfig as NaverMailConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* 웹 검색 노드 */}
            {configModalNode.type === 'web-search' && (
              <WebSearchConfigModal
                config={tempNodeConfig as WebSearchConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* MCP 서버 노드 */}
            {configModalNode.type === 'mcp' && (
              <McpConfigModal
                config={tempNodeConfig as McpConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* Google Drive 노드 */}
            {configModalNode.type === 'google-drive' && (
              <GoogleDriveConfigModal
                config={tempNodeConfig as GoogleDriveConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* Dropbox 노드 */}
            {configModalNode.type === 'dropbox' && (
              <DropboxConfigModal
                config={tempNodeConfig as DropboxConfig}
                onChange={(config) => setTempNodeConfig(config)}
              />
            )}

            {/* Notion 노드 */}
            {configModalNode.type === 'notion' && (
              <NotionConfigModal
                config={tempNodeConfig as NotionConfig}
                onChange={(config) => setTempNodeConfig(config)}
                showErrors={configModalShowErrors}
              />
            )}

        </Modal>
      )}

      {/* 저장 모달 - 이름/설명 입력 + (스케줄 워크플로우면) 실행 여부 선택 */}
      <Modal
        isOpen={showSaveConfirm}
        onClose={() => setShowSaveConfirm(false)}
        title="워크플로우 저장"
        size="sm"
        bodyClassName="wb-modal-form"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setShowSaveConfirm(false)}>취소</Button>
            <Button color="primary" disabled={saving || !saveName.trim()} onClick={async () => {
              setShowSaveConfirm(false);
              await doSave(hasScheduleTrigger ? saveScheduleActive : undefined);
            }}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-item">
            <label className='form-label'>워크플로우 이름</label>
            <input
              type="text"
              placeholder="워크플로우 이름을 입력하세요"
              value={saveName}
              autoFocus
              onChange={(e) => setSaveName(e.target.value)}
            />
          </div>
          <div className="form-item">
            <label className='form-label'>설명<span className='optional'>(선택)</span></label>
            <textarea
              className="config-textarea"
              rows={2}
              placeholder="워크플로우에 대한 설명을 입력하세요"
              value={saveDescription}
              onChange={(e) => setSaveDescription(e.target.value)}
            />
          </div>
          {hasScheduleTrigger && (
            <div className="form-item">
              <div className='form-label'>스케줄 실행
                <span className="optional">
                  스케줄 트리거의 실행 여부를 선택하세요.
                </span>
              </div>
              <div className="choice-cards choice-cards--single choice-cards--sm" role="radiogroup" aria-label="스케줄 실행 여부">
                {[
                  { value: true,  title: '스케줄 실행', desc: '저장 후 스케줄에 맞춰 자동 실행' },
                  { value: false, title: '미실행',      desc: '저장만 하고 스케줄은 정지 상태로 둠' },
                ].map((opt) => (
                  <button
                    key={opt.title}
                    type="button"
                    role="radio"
                    aria-checked={saveScheduleActive === opt.value}
                    className={`choice-card${saveScheduleActive === opt.value ? ' active' : ''}`}
                    onClick={() => setSaveScheduleActive(opt.value)}
                  >
                    <span className="choice-card-radio" />
                    <span className="choice-card-body">
                      <span className="choice-card-title">{opt.title}</span>
                      <span className="choice-card-desc">{opt.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* 예시 불러오기 확인 모달 */}
      <Modal
        isOpen={!!exampleConfirm}
        onClose={() => setExampleConfirm(null)}
        title="예시 불러오기"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="chat-confirm-body"
        footerLeft={
          <Button color="gray" appearance="outline" onClick={() => setExampleConfirm(null)}>취소</Button>
        }
        footerRight={
          <Button color="primary" onClick={confirmLoadExample}>확인</Button>
        }
      >
        현재 캔버스 내용이 예시로 교체됩니다.<br />계속하시겠습니까?
      </Modal>
	  
	  {/* 지금 실행 확인 모달 */}
	  <Modal
	  isOpen={runConfirmOpen}
	  onClose={() => setRunConfirmOpen(false)}
	  title="워크플로우 실행"
	  size="sm"
	  style={{ maxWidth: 360 }}
	  bodyClassName="chat-confirm-body"
	  footerLeft={
	  <Button color="gray" appearance="outline" onClick={() => setRunConfirmOpen(false)}>취소</Button>
	  }
	  footerRight={
	  <Button color="primary" onClick={executeManualRun} disabled={running}>
	  {running ? '실행 중...' : '지금 실행'}
	  </Button>
	  }
	  >
	  <b>"{workflow?.name}"</b> 워크플로우를 지금 바로 실행하시겠습니까?
	  </Modal>

      {/* 공유하기 모달 — 챗봇 · 템플릿 섹션별 팀 칩(chip) UI */}
      <Modal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        title="공유하기"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setShareOpen(false)}>닫기</Button>
            <Button color="primary" onClick={handleShareSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </>
        }
      >
        {workflow && (() => {
          const cu = authApi.getUser();
          const role = cu?.role || '';
          const isTeamRole = ['teamAdmin', 'user'].includes(role);
          const allTeamsFlat = shareTeamGroups.flatMap(g => g.teams);
          const getTeamName = (id: string) => allTeamsFlat.find(t => t.id === id)?.name || id;

          // 드롭다운용 팀 목록 (역할별)
          const allAvailableTeams = isTeamRole && cu?.teamId
            ? allTeamsFlat.filter(t => t.id === cu?.teamId)
            : shareCustomerId
              ? (shareTeamGroups.find(g => g.customerId === shareCustomerId)?.teams ?? [])
              : role === 'customerAdmin'
                ? (shareTeamGroups[0]?.teams ?? [])
                : allTeamsFlat;

          const renderSection = (
            sectionTitle: string,
            sectionDesc: string,
            SectionIcon: React.FC<React.SVGProps<SVGSVGElement>>,
            teamIds: string[],
            setTeamIds: React.Dispatch<React.SetStateAction<string[]>>,
            addTeam: string,
            setAddTeam: React.Dispatch<React.SetStateAction<string>>
          ) => {
            const isSharedNow = teamIds.length > 0;
            const addOptions = allAvailableTeams.filter(t => !teamIds.includes(t.id));
            return (
              <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
                {/* 섹션 헤더 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--bg-2)', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <SectionIcon style={{ width: 15, height: 15 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{sectionTitle}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1 }}>{sectionDesc}</div>
                  </div>
                  {isSharedNow
                    ? <Badge color="success" dot>공유됨</Badge>
                    : <Badge color="gray">미공유</Badge>
                  }
                </div>
                {/* 섹션 바디 */}
                <div style={{ padding: '12px 16px' }}>
                  {/* 팀 칩 */}
                  <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 7, minHeight: 28, marginBottom: 10, alignItems: 'center' }}>
                    {teamIds.length === 0
                      ? <span style={{ fontSize: 12, color: 'var(--text-3)', fontStyle: 'italic' }}>팀을 추가하면 공유됩니다.</span>
                      : teamIds.map(id => (
                          <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--accent-soft)', border: '1px solid rgba(99,102,241,0.25)', color: 'var(--accent)', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20 }}>
                            {getTeamName(id)}
                            <button
                              onClick={() => setTeamIds(prev => prev.filter(i => i !== id))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(99,102,241,0.5)', fontSize: 12, lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center' }}
                            >✕</button>
                          </span>
                        ))
                    }
                  </div>
                  {/* 팀 추가 드롭다운 */}
                  {addOptions.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Select
                        value={addTeam}
                        onChange={e => setAddTeam(e.target.value)}
                        style={{ flex: 1, fontSize: 12.5 }}
                      >
                        <option value="">팀 추가...</option>
                        {addOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </Select>
                      <Button
                        color="primary"
                        appearance="outline"
                        size="sm"
                        onClick={() => { if (!addTeam) return; setTeamIds(prev => [...prev, addTeam]); setAddTeam(''); }}
                        disabled={!addTeam}
                      >
                        + 추가
                      </Button>
                    </div>
                  )}
                  {addOptions.length === 0 && allAvailableTeams.length > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>모든 팀이 추가됨</span>
                  )}
                </div>
              </div>
            );
          };

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* 고객사 선택 (productAdmin/tenantAdmin 전용) */}
              {['productAdmin', 'tenantAdmin', 'admin'].includes(role) && (
                <div className="form-item">
                  <div className="form-label">고객사</div>
                  <Select
                    value={shareCustomerId}
                    onChange={e => setShareCustomerId(e.target.value)}
                  >
                    <option value="">전체 고객사</option>
                    {shareTeamGroups.map(g => (
                      <option key={g.customerId} value={g.customerId}>{g.customerName}</option>
                    ))}
                  </Select>
                </div>
              )}
              {/* 챗봇 섹션 */}
              {canShareChatbot && renderSection(
                '챗봇으로 공유',
                '사용자 포털 챗봇 목록에 올려 구성원이 대화할 수 있게 합니다.',
                ChatIcon,
                chatbotTeamIds,
                setChatbotTeamIds,
                chatbotAddTeam,
                setChatbotAddTeam
              )}
              {/* 템플릿 섹션 */}
              {renderSection(
                '템플릿으로 공유',
                '워크플로우 템플릿 목록에 올려 다른 사람이 복사해 쓸 수 있게 합니다.',
                ShareIcon,
                templateTeamIds,
                setTemplateTeamIds,
                templateAddTeam,
                setTemplateAddTeam
              )}
            </div>
          );
        })()}
      </Modal>

      {/* 워크플로우 정보 수정 모달 */}
      <Modal
        isOpen={showInfoModal}
        onClose={closeInfoModal}
        title="워크플로우 정보 수정"
        size="sm"
        bodyClassName="wb-modal-form"
        style={{ transform: `translate(${infoModalPos.x}px, ${infoModalPos.y}px)` }}
        headerProps={{ style: { cursor: 'move' }, onMouseDown: (e) => handleModalDragStart(e, 'info') }}
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeInfoModal}>취소</Button>
            <Button
              color="primary"
              onClick={saveWorkflowInfo}
              disabled={savingInfo || !tempWorkflowInfo.name.trim()}
            >
              {savingInfo ? '저장 중...' : '저장'}
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-item">
            <label className="form-label">워크플로우 이름 <span className="required">*</span></label>
            <div className="input">
              <input
                type="text"
                className="input-control"
                placeholder="워크플로우 이름을 입력하세요"
                value={tempWorkflowInfo.name}
                onChange={(e) => setTempWorkflowInfo({ ...tempWorkflowInfo, name: e.target.value })}
              />
            </div>
          </div>

          <div className="form-item">
            <label className="form-label">설명<span className='optional'>(선택)</span></label>
            <textarea
              className="config-textarea"
              rows={3}
              placeholder="워크플로우에 대한 설명을 입력하세요"
              value={tempWorkflowInfo.description}
              onChange={(e) => setTempWorkflowInfo({ ...tempWorkflowInfo, description: e.target.value })}
            />
          </div>
        </div>
      </Modal>

      {/* 복사하기 - 커넥터 안내 모달 */}
      <Modal
        isOpen={showCopyConnectorModal}
        onClose={() => setShowCopyConnectorModal(false)}
        title="복사 전 안내"
        size="sm"
        style={{ maxWidth: 420 }}
        bodyClassName="dialog-body--compact"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setShowCopyConnectorModal(false)}>취소</Button>
            <Button color="primary" onClick={() => { setShowCopyConnectorModal(false); openAsDraft(); }}>확인 후 복사</Button>
          </>
        }
      >
        <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.7 }}>
          이 템플릿에는 커넥터 노드(Slack, Notion, Google Drive 등)가 포함되어 있습니다.
          <br /><br />
          복사 후 워크플로우 빌더에서 <strong>각 커넥터를 본인 계정으로 설정</strong>하시길 바랍니다.
        </p>
      </Modal>
    </>
  );
}

export default WorkflowBuilderPage;
